import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { supabase } from './supabase'
import { FACT_FINDS } from './factFinds'
import FactFindSection from './FactFindSection'
import { CoverConfirmedModal, CoversTab } from './CoverConfirmed'
import { QuotePackModal } from './QuotePackModal'
import { SmsComposeModal } from './SmsComposeModal'
import { StatusReasonModal } from './StatusReasonModal'
import {
  buildComplianceState,
  buildRequiredInfoCompletionUpdates,
  logClientEmailAction,
  makeActivityEntry,
  makeAuditEntry,
  persistClientCompliance,
} from './compliance'

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const LEAD_STATUSES = ['New Lead','In Progress','Quote Sent','Awaiting Client','Under Referral','No Contact 1','No Contact 2','No Contact 3','Policy Placed','Not Interested']
const REASON_REQUIRED_STATUSES = ['Not Interested']
const SMS_TRIGGER_STATUSES = ['Quote Sent','No Contact 1','No Contact 2','No Contact 3','Policy Placed','Awaiting Client']
const BROKERS = ['Ben Rogers','Jesse Roach','Mitch Speer']
const BROKER_EMAILS = {
  'Ben Rogers': 'ben@benchmarkinsurance.com.au',
  'Jesse Roach': 'jesse@benchmarkinsurance.com.au',
  'Mitch Speer': 'mitch@benchmarkinsurance.com.au',
}
const FACT_FIND_OPTIONS = Object.keys(FACT_FINDS)
const STATUS_COLORS = {
  'New Lead':'#1e9e75','In Progress':'#e6a817','Quote Sent':'#3b82f6','Awaiting Client':'#e6a817',
  'Under Referral':'#8b5cf6','No Contact 1':'#f97316','No Contact 2':'#ea580c',
  'No Contact 3':'#c2410c','Policy Placed':'#1e3d30','Not Interested':'#dc3545',
}
const DOCUMENT_PREFIX = 'client-documents'
const LOGO_URL = 'https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets/BIB-Dark-(Horizontal).png'

const AUDIT_ICONS = {
  note:'📝', edit:'✏️', status:'🔄', factfind:'📋', sms:'💬',
  email:'📧', compliance:'⚖️', document:'📄', quote:'📦',
  coverconfirmed:'✅', loa:'🖊️', call:'📞', audit:'🔹', contacted:'📲',
}

const EXCLUDED_FF_KEYS = new Set(['ff_notes','ff_additional_trucks','ff_authorised_contact','ff_advisor_summary','ff_other_disclosure','ff_notes'])
// A field counts toward completion only if it actually renders: not conditional, not legacy, not an excluded free-text field.
// MUST mirror FactFindSection's visibility logic — otherwise % drifts (e.g. a hidden legacy field caps completion below 100%).
const isCountableFfField = (field) => !field.showIf && !field.legacy && !EXCLUDED_FF_KEYS.has(field.key)
const RETAIL_TYPES = ['Car','Commercial Motor','Motorbike','Caravan','Boat','Property','Landlord','Farm','PAC','Home & Contents']
const CLAIMS_MADE_TYPES = ['Professional Indemnity','Management Liability','Cyber','Directors & Officers']

const FF_ICONS = { Truck:'🚛','Public Liability':'🦺',Landlord:'🏠','Commercial Property':'🏢','Plant & Equipment':'🔧',Car:'🚗',Boat:'⛵',Caravan:'🚐',Motorbike:'🏍️',Farm:'🌾',Carriers:'📦',PAC:'✈️','Workers Compensation':'👷','Professional Indemnity':'⚖️',Construction:'🏗️','Commercial Motor':'🚐','Annual Works':'🏗️','Single Project':'🏗️','Owner Builder':'🔨',Endorsement:'📝' }

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getTabMemory = (clientId) => { try { return sessionStorage.getItem(`tab_${clientId}`) || 'overview' } catch { return 'overview' } }
const setTabMemory = (clientId, tab) => { try { sessionStorage.setItem(`tab_${clientId}`, tab) } catch {} }
const getFfMemory = (clientId) => { try { return JSON.parse(sessionStorage.getItem(`ff_${clientId}`) || 'null') } catch { return null } }
const setFfMemory = (clientId, ffId, ffType) => { try { sessionStorage.setItem(`ff_${clientId}`, JSON.stringify({ffId, ffType})) } catch {} }
const clearFfMemory = (clientId) => { try { sessionStorage.removeItem(`ff_${clientId}`) } catch {} }

const normaliseAuPhone = (phone) => {
  const raw = (phone || '').replace(/\s+/g, '').trim()
  if (!raw) return ''
  if (raw.startsWith('+')) return raw
  if (raw.startsWith('61')) return `+${raw}`
  if (raw.startsWith('0')) return `+61${raw.slice(1)}`
  return raw
}

const buildDocumentPath = (clientId, fileName) => {
  const safeName = (fileName || 'document').replace(/[^\w.\- ]/g, '').replace(/\s+/g, '-')
  return `${DOCUMENT_PREFIX}/${clientId}/${Date.now()}-${safeName}`
}

const fmtDuration = (seconds) => {
  if (!seconds) return '0s'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

const isRetailType = (ct) => RETAIL_TYPES.some(t => (ct||'').toLowerCase().includes(t.toLowerCase()))
const isClaimsMadeType = (ct) => CLAIMS_MADE_TYPES.some(t => (ct||'').toLowerCase().includes(t.toLowerCase()))

const getDraftKey = (clientId, ffId) => `ff_draft_${clientId}_${ffId}`
const saveDraft = (clientId, ffId, data) => { try { localStorage.setItem(getDraftKey(clientId, ffId), JSON.stringify(data)) } catch {} }
const loadDraft = (clientId, ffId) => { try { const raw = localStorage.getItem(getDraftKey(clientId, ffId)); return raw ? JSON.parse(raw) : null } catch { return null } }
const clearDraft = (clientId, ffId) => { try { localStorage.removeItem(getDraftKey(clientId, ffId)) } catch {} }

const generateFfId = (type) => `${type.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now()}`

// ── Push notifications ────────────────────────────────────────────────────────
async function requestPushPermission() {
  if (!('Notification' in window)) return false
  if (Notification.permission === 'granted') return true
  const result = await Notification.requestPermission()
  return result === 'granted'
}

function sendPushNotification(title, body, onClick) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  const n = new Notification(title, { body, icon: LOGO_URL, badge: LOGO_URL, tag: 'benchmark-sms', renotify: true })
  if (onClick) n.onclick = () => { window.focus(); onClick(); n.close() }
}

// ── CopyChip ──────────────────────────────────────────────────────────────────
function CopyChip({ value, label, icon }) {
  const [copied, setCopied] = useState(false)
  if (!value) return <span style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>{label || 'Not set'}</span>
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1800) }}
      title={`Copy ${value}`}
      style={{
        background: copied ? 'rgba(30,61,48,0.1)' : 'rgba(0,0,0,0.03)',
        border: `1px solid ${copied ? 'var(--forest-border)' : 'rgba(0,0,0,0.06)'}`,
        borderRadius: 6, padding: '3px 10px', fontSize: 12,
        color: copied ? 'var(--forest)' : 'var(--text)',
        cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all .15s',
        display: 'inline-flex', alignItems: 'center', gap: 5,
      }}
    >
      {copied ? '✓' : (icon || '⎘')} {value}
    </button>
  )
}

// ── ProgressRing ──────────────────────────────────────────────────────────────
function ProgressRing({ pct, size = 40, stroke = 3.5 }) {
  const radius = (size - stroke) / 2
  const circ = 2 * Math.PI * radius
  const offset = circ - (pct / 100) * circ
  const color = pct === 100 ? 'var(--forest)' : pct > 60 ? '#e6a817' : 'var(--muted)'
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="var(--border)" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset .4s ease' }} />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center', fontSize: size * 0.26, fontWeight: 700, fill: color, fontFamily: 'var(--font)' }}>
        {pct}%
      </text>
    </svg>
  )
}

// ── ComplianceProgress ────────────────────────────────────────────────────────
function ComplianceProgress({ complianceState }) {
  const items = [
    { label: 'Required Info', done: !!complianceState.important_info_sent_at, date: complianceState.important_info_sent_at },
    { label: 'Quote Pack', done: !!complianceState.quote_pack_sent_at, date: complianceState.quote_pack_sent_at },
  ]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
      {items.map(item => (
        <div key={item.label} style={{
          background: item.done ? 'rgba(30,61,48,0.05)' : 'var(--surface)',
          border: `1.5px solid ${item.done ? 'var(--forest-border)' : 'var(--border)'}`,
          borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{
            width: 22, height: 22, borderRadius: 6,
            background: item.done ? 'var(--forest)' : 'var(--surface2)',
            border: `2px solid ${item.done ? 'var(--forest)' : 'var(--border)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 11, flexShrink: 0,
          }}>{item.done ? '✓' : ''}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: item.done ? 'var(--forest)' : 'var(--muted)' }}>{item.label}</div>
            {item.done && item.date && (
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 1 }}>
                {new Date(item.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
              </div>
            )}
          </div>
          {item.done && <span style={{ fontSize: 10, color: 'var(--forest)', fontWeight: 700, flexShrink: 0 }}>✓ Sent</span>}
        </div>
      ))}
    </div>
  )
}

// ── TranscriptBlock ───────────────────────────────────────────────────────────
function TranscriptBlock({ transcript, search }) {
  const [open, setOpen] = useState(false)
  const highlight = (text) => {
    if (!search || !search.trim()) return text
    const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
    return parts.map((part, i) =>
      part.toLowerCase() === search.toLowerCase()
        ? <mark key={i} style={{ background: 'rgba(242,101,34,0.35)', borderRadius: 2, padding: '0 1px' }}>{part}</mark>
        : part
    )
  }
  return (
    <div>
      <button className="btn btn-secondary btn-sm" style={{ width: '100%', justifyContent: 'space-between', marginTop: 6 }} onClick={() => setOpen(o => !o)}>
        <span>📄 View transcript</span><span>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 8, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', maxHeight: 300, overflowY: 'auto' }}>
          {transcript.split('\n').map((line, i) => (
            <div key={i} style={{ fontSize: 12, lineHeight: 1.8, marginBottom: 3 }}>{highlight(line)}</div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── ClientDocumentsBox ────────────────────────────────────────────────────────
function ClientDocumentsBox({ documents, loading, uploading, onFilesSelected, onRename }) {
  const [dragOver, setDragOver] = useState(false)
  const [renamingPath, setRenamingPath] = useState(null)
  const [renameValue, setRenameValue] = useState('')
  const inputRef = useRef(null)
  const handleFiles = (fileList) => { const files = Array.from(fileList || []); if (files.length > 0) onFilesSelected(files) }
  const startRename = (doc) => { setRenamingPath(doc.path); setRenameValue(doc.displayName || doc.name) }
  const commitRename = (doc) => {
    if (renameValue.trim() && renameValue.trim() !== (doc.displayName || doc.name)) onRename(doc, renameValue.trim())
    setRenamingPath(null); setRenameValue('')
  }
  const formatSize = (bytes) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes}B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  }
  return (
    <div>
      <div onDragOver={e => { e.preventDefault(); setDragOver(true) }} onDragLeave={() => setDragOver(false)} onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files) }} onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--forest)' : 'var(--forest-border)'}`,
          borderRadius: 14, padding: '28px 20px',
          background: dragOver ? 'rgba(30,61,48,0.08)' : 'linear-gradient(135deg,rgba(30,61,48,0.03),rgba(242,101,34,0.03))',
          cursor: 'pointer', marginBottom: 16, textAlign: 'center',
          transition: 'all .2s',
        }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>📁</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--forest)', marginBottom: 4 }}>Drop files here or click to browse</div>
        <div style={{ fontSize: 11, color: uploading ? 'var(--forest)' : 'var(--muted)', fontWeight: uploading ? 600 : 400 }}>{uploading ? 'Uploading...' : 'PDF, Word, Excel, images, emails'}</div>
        <input ref={inputRef} type="file" multiple accept=".pdf,.eml,.msg,.txt,.doc,.docx,.jpg,.jpeg,.png,.heic,.xlsx,.xls" style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
      </div>
      {loading
        ? <div className="loading"><div className="spinner" /><span>Loading documents...</span></div>
        : documents.length === 0
          ? <div className="empty" style={{ padding: '32px 24px' }}><div className="empty-icon">📁</div><div className="empty-title">No documents stored yet</div><div className="empty-sub">Upload client emails, signed forms or compliance files here</div></div>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {documents.map(doc => (
                <div key={doc.path} style={{
                  background: 'var(--surface)', border: '1.5px solid var(--border)',
                  borderRadius: 12, padding: '12px 16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'border-color .15s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 20, flexShrink: 0 }}>📄</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {renamingPath === doc.path ? (
                        <input autoFocus className="form-input" style={{ fontSize: 13, fontWeight: 600, padding: '4px 8px', height: 30 }} value={renameValue} onChange={e => setRenameValue(e.target.value)} onBlur={() => commitRename(doc)} onKeyDown={e => { if (e.key === 'Enter') commitRename(doc); if (e.key === 'Escape') { setRenamingPath(null); setRenameValue('') } }} onClick={e => e.stopPropagation()} />
                      ) : (
                        <div style={{ fontSize: 13, fontWeight: 600, cursor: 'text', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title="Click to rename" onClick={e => { e.stopPropagation(); startRename(doc) }}>
                          {doc.displayName || doc.name}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, display: 'flex', gap: 8 }}>
                        {doc.created_at && <span>{new Date(doc.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                        {doc.metadata?.size && <span>{formatSize(doc.metadata.size)}</span>}
                      </div>
                    </div>
                  </div>
                  <a className="btn btn-secondary btn-sm" href={doc.url} target="_blank" rel="noreferrer">Open</a>
                </div>
              ))}
            </div>
      }
    </div>
  )
}

// ── CallScriptModal ───────────────────────────────────────────────────────────
function CallScriptModal({ onClose, policyType, initialTab = 'opening' }) {
  const detectedType = isClaimsMadeType(policyType || '') ? 'claimsmade' : isRetailType(policyType || '') ? 'retail' : 'wholesale'
  const [scriptType, setScriptType] = useState(detectedType)
  const [activeTab, setActiveTab] = useState(initialTab)
  const [closingPaymentType, setClosingPaymentType] = useState('annual')

  const SCRIPT_TYPES = [
    { key: 'retail', label: '🏠 Retail' },
    { key: 'wholesale', label: '🏢 Wholesale' },
    { key: 'claimsmade', label: '⚖️ Claims-Made' },
  ]

  const openingScripts = {
retail: [
      { label: 'Recording Notice', style: 'forest', text: 'All of our calls are recorded. The information you provide me with will be provided to the insurers with whom we will speak.' },
      { label: 'Duty to Take Reasonable Care', style: 'neutral', text: 'Now you must take reasonable care not to make a misrepresentation to your insurer. This responsibility is to anyone who will be insured under the policy. You must answer our questions honestly, accurately, and to the best of your knowledge. A misrepresentation is an answer that is false, misleading, inaccurate, incomplete, or dishonest. If you do not meet the above duty, your insurer may reject or not fully pay your claim or cancel your policy.' },
     { label: 'Commission Consent', style: 'forest', text: 'Before we go further, I need to confirm one thing about how we are paid. Benchmark receives a commission from the insurer for arranging your cover. Do you consent to us receiving that commission? … Thank you, I have noted your consent.' },
    ],
    wholesale: [
      { label: 'Recording Notice', style: 'forest', text: 'Just to let you know all our calls are recorded for training and quality. The information you provide will be provided to the insurance companies with whom we will speak.' },
      { label: 'Duty of Disclosure', style: 'neutral', text: 'I will also let you know about your duty of disclosure. When answering questions, you must be honest and tell us anything a reasonable person would include. Your insurance company may reduce or refuse a claim if you have not answered these questions in this way.' },
    ],
    claimsmade: [
      { label: 'Recording Notice', style: 'forest', text: 'Just to let you know all our calls are recorded for training and quality. The information you provide will be provided to the insurance companies with whom we will speak.' },
      { label: 'Duty of Disclosure', style: 'neutral', text: 'I will also let you know about your duty of disclosure. When answering questions, you must be honest and tell us anything a reasonable person would include. Your insurance company may reduce or refuse a claim if you have not answered these questions in this way.' },
      { label: "Claims-Made Policy — Important Notice", style: 'warning', text: "This is a claims-made policy. This means that any claim made against you, or any incident that could potentially give rise to a claim, must be notified to the insurer during the period of insurance — not just when the event occurred. If you do not notify during the current period, you may have no cover for the claim, even if the event happened while the policy was active." },
    ],
  }

  const sectionStyle = (style) => {
    if (style === 'forest') return { background: 'rgba(30,61,48,0.07)', border: '1.5px solid var(--forest-border)', borderRadius: 12, padding: '16px 18px' }
    if (style === 'warning') return { background: 'rgba(139,92,246,0.07)', border: '1.5px solid rgba(139,92,246,0.25)', borderRadius: 12, padding: '16px 18px' }
    return { background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }
  }

  const labelStyle = (style) => {
    if (style === 'forest') return { color: 'var(--forest)' }
    if (style === 'warning') return { color: '#7c3aed' }
    return { color: 'var(--muted)' }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 660 }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="modal-title">{activeTab === 'opening' ? '📋 Call Opening Script' : '🔚 Call Closing Script'}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>Read aloud {activeTab === 'opening' ? 'at the start' : 'at the end'} of each call</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, background: 'var(--surface2)', borderRadius: 10, padding: 4 }}>
            {[{ key: 'opening', label: '📋 Opening' }, { key: 'closing', label: '🔚 Closing' }].map(({ key, label }) => (
              <button key={key} onClick={() => setActiveTab(key)} style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: 'none', background: activeTab === key ? 'var(--forest)' : 'transparent', color: activeTab === key ? '#fff' : 'var(--muted)', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all .15s' }}>{label}</button>
            ))}
          </div>

          {activeTab === 'opening' ? (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {SCRIPT_TYPES.map(({ key, label }) => (
                  <button key={key} onClick={() => setScriptType(key)} style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: `2px solid ${scriptType === key ? 'var(--forest)' : 'var(--border)'}`, background: scriptType === key ? 'var(--forest)' : 'var(--surface2)', color: scriptType === key ? '#fff' : 'var(--muted)', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}>{label}</button>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {openingScripts[scriptType].map((section, i) => (
                  <div key={i} style={sectionStyle(section.style)}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 8, ...labelStyle(section.style) }}>{section.label}</div>
                    <div style={{ fontSize: 14, lineHeight: 1.85, color: '#1a2e25' }}>{section.text}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {[{ key: 'annual', label: '💳 Annual Payment' }, { key: 'monthly', label: '📅 Monthly (Premium Funding)' }].map(({ key, label }) => (
                  <button key={key} onClick={() => setClosingPaymentType(key)} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `2px solid ${closingPaymentType === key ? 'var(--forest)' : 'var(--border)'}`, background: closingPaymentType === key ? 'var(--forest)' : 'var(--surface2)', color: closingPaymentType === key ? '#fff' : 'var(--muted)', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all .15s' }}>{label}</button>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={sectionStyle('forest')}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 8, color: 'var(--forest)' }}>Opening</div>
                  <div style={{ fontSize: 14, lineHeight: 1.85, color: '#1a2e25' }}>
                    We're ready to arrange your cover. Subject to your confirmation, your cover will commence from <strong>[start date]</strong>, and all policy documents will be emailed to you shortly.
                  </div>
                </div>

                <div style={sectionStyle('neutral')}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10, color: 'var(--muted)' }}>Key Policy Summary</div>
                  {[['Insurer', '[Insurer Name]'], ['Cover Type', '[Cover Type]'], ['Total Invoice', '[Amount]']].map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', gap: 12, fontSize: 13, marginBottom: 6 }}>
                      <div style={{ width: 120, fontWeight: 600, color: 'var(--muted)', flexShrink: 0 }}>{label}</div>
                      <div style={{ color: 'var(--text)', fontWeight: 500 }}>{value}</div>
                    </div>
                  ))}
                </div>

                <div style={sectionStyle('forest')}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 8, color: 'var(--forest)' }}>Placement Consent</div>
                  <div style={{ fontSize: 14, lineHeight: 1.85, color: '#1a2e25' }}>
                    Before I arrange this, I just need to confirm: are you happy for us to place your cover with <strong>[Insurer Name]</strong>? … Thank you. I'll now arrange the cover and send your documents through.
                  </div>
                </div>

                <div style={sectionStyle('neutral')}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 8, color: 'var(--muted)' }}>Payment</div>
                  {closingPaymentType === 'annual' ? (
                    <div style={{ fontSize: 14, lineHeight: 1.85, color: '#1a2e25' }}>
                      You can pay the premium in full via <strong>BPAY or card</strong> using the invoice we send through.
                    </div>
                  ) : (
                    <div style={{ fontSize: 14, lineHeight: 1.85, color: '#1a2e25' }}>
                      You've chosen to pay <strong>monthly via premium funding</strong>. They will pay the premium in full to the insurer, and you will repay them in instalments. Please note that <strong>interest applies</strong> to this arrangement.<br/><br/>
                      Early cancellation can incur cancellation costs and possible shortfalls, so please read the documentation for more information.
                    </div>
                  )}
                </div>

                <div style={sectionStyle('neutral')}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 8, color: 'var(--muted)' }}>Closing</div>
                  <div style={{ fontSize: 14, lineHeight: 1.85, color: '#1a2e25' }}>
                    Once you receive your documents, please review your <strong>record of answers</strong> to ensure all information is correct. You may also receive a <strong>DocuSign</strong> to complete your Record of Advice.<br/><br/>
                    We'll send everything through shortly — if you have any questions at all, please don't hesitate to reach out.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// Formats a field's raw value into readable text for the read-only view and printed
// ROA. Most fields are plain strings or string arrays (joined with commas) — but
// repeatable groups like the Truck Details field store an ARRAY OF OBJECTS
// ({rego, makeModelYear, gvm, sumInsured} per truck). Without this check, that array
// falls through to the generic .join(', ') below, which stringifies each object as
// "[object Object]" since objects don't have a meaningful toString(). We use the
// field's own subFields definition (from factFinds.js) to know which keys to pull
// and in what order, so this works for any future repeatable-group field too.
function formatFfFieldValue(field, val, lineJoin = ', ') {
  if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object' && val[0] !== null && field?.subFields) {
    return val.map((item, i) => {
      const parts = field.subFields.map(sf => item[sf.key]).filter(Boolean)
      return `Truck ${i + 1}: ${parts.join(' · ')}`
    }).join(lineJoin)
  }
  if (Array.isArray(val)) return val.join(', ')
  return String(val)
}

// ── ROA Print ─────────────────────────────────────────────────────────────────
function printFactFindAsRoa({ ffData, ffType, clientData, brokerName }) {
  const template = FACT_FINDS[ffType]
  if (!template) return
  const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const rows = []
  template.sections.forEach(section => {
    let sectionHasContent = false
    const sectionRows = []
    section.fields.forEach(field => {
      if (field.showIf && ffData[field.showIf.key] !== field.showIf.value) return
      const val = ffData[field.key]
      if (!val || val === '' || (Array.isArray(val) && val.length === 0)) return
      const displayVal = formatFfFieldValue(field, val, '<br/>')
      sectionRows.push({ label: field.label, value: displayVal })
      sectionHasContent = true
    })
    if (sectionHasContent) {
      rows.push({ isSection: true, label: section.title || section.label || 'Section' })
      sectionRows.forEach(r => rows.push(r))
    }
  })
  const rowsHtml = rows.map(r => {
    if (r.isSection) return `<tr><td colspan="2" style="background:#1e3d30;color:#fff;font-size:11px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;padding:8px 14px;">${r.label}</td></tr>`
    return `<tr><td style="padding:8px 14px;font-weight:600;color:#4a5e55;width:40%;border-bottom:1px solid #eee;font-size:13px;">${r.label}</td><td style="padding:8px 14px;color:#1a2e25;border-bottom:1px solid #eee;font-size:13px;">${r.value}</td></tr>`
  }).join('')
  const html = `<!DOCTYPE html><html><head><title>Record of Answers — ${clientData.company_name}</title><style>*{box-sizing:border-box}body{margin:0;font-family:Arial,sans-serif;color:#1a2e25;background:#fff}.page{width:210mm;min-height:297mm;margin:0 auto}.header{background:#1e3d30;padding:24px 32px;display:flex;justify-content:space-between;align-items:center}.header img{height:44px}.header-right{text-align:right;color:rgba(255,255,255,0.8);font-size:12px;line-height:1.7}.title-bar{background:#f0f4f2;border-bottom:2px solid #d7e5de;padding:16px 32px}.title-bar h1{margin:0;font-size:18px;color:#1e3d30;font-weight:800}.title-bar p{margin:4px 0 0;font-size:12px;color:#6b8278}.body{padding:24px 32px}table{width:100%;border-collapse:collapse;margin-bottom:24px;border:1px solid #e2e8e5;border-radius:8px;overflow:hidden}.footer{margin-top:32px;padding:16px 32px;border-top:1px solid #d7e5de;background:#f0f4f2;font-size:11px;color:#6b8278;line-height:1.7}.print-bar{position:fixed;top:16px;right:16px;z-index:999;display:flex;gap:8px}.print-btn{background:#1e3d30;color:#fff;border:none;padding:10px 20px;border-radius:10px;font-size:14px;font-weight:700;cursor:pointer}.close-btn{background:#6b8278;color:#fff;border:none;padding:10px 20px;border-radius:10px;font-size:14px;font-weight:700;cursor:pointer}@media print{.print-bar{display:none!important}@page{size:A4;margin:0}}</style></head><body><div class="print-bar"><button class="print-btn" onclick="window.print()">🖨️ Print / Save PDF</button><button class="close-btn" onclick="window.close()">Close</button></div><div class="page"><div class="header"><img src="${LOGO_URL}" alt="Benchmark Insurance Brokers"/><div class="header-right"><strong style="color:#fff;">Record of Answers</strong><br/>${today}<br/>Broker: ${brokerName}</div></div><div class="title-bar"><h1>${clientData.company_name}</h1><p>${ffType} Fact Find · Prepared by ${brokerName} · ${today}</p></div><div class="body"><table><tbody>${rowsHtml}</tbody></table></div><div class="footer">This Record of Answers was captured by <strong>${brokerName}</strong> on <strong>${today}</strong> and represents the information provided by the client at the time of this conversation.<br/><br/><strong>Benchmark Insurance Group Pty Ltd</strong> trading as <strong>Benchmark Insurance Brokers</strong> | ABN 33 694 361 549 | AR No. 001320555<br/>Authorised Representative of <strong>McLardy McShane Partners Pty Ltd</strong> | ABN 14 064 465 309 | AFSL 232987</div></div></body></html>`
  const w = window.open('', '_blank', 'width=1000,height=900')
  if (!w) return
  w.document.write(html)
  w.document.close()
  w.focus()
}

// ── OverviewFeed ──────────────────────────────────────────────────────────────
const FEED_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'sms', label: '💬 SMS' },
  { key: 'call', label: '📞 Calls' },
  { key: 'note', label: '📝 Notes' },
  { key: 'compliance', label: '⚖️ Compliance' },
  { key: 'quote', label: '📦 Quotes' },
]

const FEED_ICONS = {
  note: { icon: '📝', bg: 'rgba(107,130,120,0.1)', color: 'var(--muted)' },
  sms: { icon: '💬', bg: 'rgba(59,130,246,0.1)', color: '#2563eb' },
  call: { icon: '📞', bg: 'rgba(30,61,48,0.1)', color: 'var(--forest)' },
  status: { icon: '🔄', bg: 'rgba(139,92,246,0.1)', color: '#7c3aed' },
  factfind: { icon: '📋', bg: 'rgba(30,61,48,0.08)', color: 'var(--forest)' },
  quote: { icon: '📦', bg: 'rgba(242,101,34,0.1)', color: 'var(--orange)' },
  coverconfirmed: { icon: '✅', bg: 'rgba(30,61,48,0.1)', color: 'var(--forest)' },
  email: { icon: '📧', bg: 'rgba(59,130,246,0.08)', color: '#2563eb' },
  compliance: { icon: '⚖️', bg: 'rgba(139,92,246,0.08)', color: '#7c3aed' },
  document: { icon: '📄', bg: 'rgba(107,130,120,0.08)', color: 'var(--muted)' },
  loa: { icon: '🖊️', bg: 'rgba(30,61,48,0.08)', color: 'var(--forest)' },
  contacted: { icon: '📲', bg: 'rgba(30,61,48,0.08)', color: 'var(--forest)' },
  audit: { icon: '🔹', bg: 'var(--surface2)', color: 'var(--muted)' },
}

function fmtTimeAgoFeed(d) {
  if (!d) return '—'
  const mins = Math.floor((new Date() - new Date(d)) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

function OverviewFeed({ auditEvents, clientTexts, clientCalls, handleTabChange }) {
  const [filter, setFilter] = useState('all')
  const [limit, setLimit] = useState(20)

  const feed = useMemo(() => {
    const items = [...auditEvents]

    // Merge in texts not already in audit
    const auditTextIds = new Set(auditEvents.filter(e => e.type === 'sms').map(e => e.key))
    clientTexts.forEach(t => {
      const key = `text-${t.id}`
      if (!auditTextIds.has(key)) {
        items.push({
          key, type: 'sms',
          timestamp: t.created_at,
          title: t.direction === 'outbound' ? 'SMS sent' : 'SMS received',
          actor: t.broker_name || (t.direction === 'inbound' ? 'Client' : 'Broker'),
          changes: [t.body || ''],
          direction: t.direction,
        })
      }
    })

    // Merge in calls not already in audit
    const auditCallIds = new Set(auditEvents.filter(e => e.type === 'call').map(e => e.key))
    clientCalls.forEach(c => {
      const key = `call-${c.id}`
      if (!auditCallIds.has(key)) {
        items.push({
          key, type: 'call',
          timestamp: c.started_at,
          title: c.direction === 'inbound' ? 'Inbound call' : 'Outbound call',
          actor: c.broker_name || 'Phone',
          changes: [c.duration ? `${Math.floor(c.duration / 60)}m ${c.duration % 60}s` : 'Missed', c.transcript ? 'Transcript available' : null].filter(Boolean),
          missed: !c.duration || c.duration < 5,
        })
      }
    })

    return items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  }, [auditEvents, clientTexts, clientCalls])

  const filterMap = {
    all: () => true,
    sms: e => e.type === 'sms',
    call: e => e.type === 'call',
    note: e => e.type === 'note',
    compliance: e => ['compliance', 'email', 'loa', 'coverconfirmed'].includes(e.type),
    quote: e => ['quote', 'coverconfirmed'].includes(e.type),
  }

  const filtered = feed.filter(filterMap[filter] || (() => true))
  const visible = filtered.slice(0, limit)

  return (
    <div>
      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {FEED_FILTERS.map(({ key, label }) => {
          const count = feed.filter(filterMap[key] || (() => true)).length
          return (
            <button
              key={key}
              onClick={() => { setFilter(key); setLimit(20) }}
              style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                border: `1.5px solid ${filter === key ? 'var(--forest)' : 'var(--border)'}`,
                background: filter === key ? 'var(--forest)' : 'var(--surface)',
                color: filter === key ? '#fff' : 'var(--muted)',
                cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all .12s',
              }}
            >
              {label}{key !== 'all' && count > 0 ? ` (${count})` : ''}
            </button>
          )
        })}
      </div>

      {/* Feed items */}
      {visible.length === 0 ? (
        <div className="empty" style={{ padding: '32px 24px' }}>
          <div className="empty-icon">📭</div>
          <div className="empty-title">No activity yet</div>
          <div className="empty-sub">Notes, calls, texts and status changes appear here</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {visible.map((entry, i) => {
            const meta = FEED_ICONS[entry.type] || FEED_ICONS.audit
            const isInboundSms = entry.type === 'sms' && entry.direction === 'inbound'
            const isMissedCall = entry.type === 'call' && entry.missed
            const preview = entry.changes?.[0] || ''

            return (
              <div
                key={entry.key}
                onClick={() => {
                  if (entry.type === 'sms') handleTabChange('texts')
                  else if (entry.type === 'call') handleTabChange('calls')
                  else if (entry.type === 'quote') handleTabChange('quotes')
                  else if (entry.type === 'coverconfirmed') handleTabChange('coverconfirmed')
                  else if (entry.type === 'factfind') handleTabChange('factfinds')
                  else if (entry.type === 'document') handleTabChange('documents')
                }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 12px',
                  borderRadius: 10,
                  cursor: ['sms', 'call', 'quote', 'coverconfirmed', 'factfind', 'document'].includes(entry.type) ? 'pointer' : 'default',
                  background: isInboundSms ? 'rgba(59,130,246,0.03)' : isMissedCall ? 'rgba(220,53,69,0.02)' : 'transparent',
                  border: `1px solid ${isInboundSms ? 'rgba(59,130,246,0.1)' : isMissedCall ? 'rgba(220,53,69,0.08)' : 'transparent'}`,
                  marginBottom: 2,
                  transition: 'background .1s',
                }}
                onMouseEnter={e => { if (['sms','call','quote','coverconfirmed','factfind','document'].includes(entry.type)) e.currentTarget.style.background = 'var(--surface2)' }}
                onMouseLeave={e => { e.currentTarget.style.background = isInboundSms ? 'rgba(59,130,246,0.03)' : isMissedCall ? 'rgba(220,53,69,0.02)' : 'transparent' }}
              >
                {/* Icon */}
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: isMissedCall ? 'rgba(220,53,69,0.1)' : meta.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, flexShrink: 0, marginTop: 1,
                }}>
                  {isMissedCall ? '📵' : meta.icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{
                      fontSize: 12, fontWeight: 600,
                      color: isMissedCall ? 'var(--danger)' : isInboundSms ? '#2563eb' : 'var(--text)',
                    }}>
                      {entry.title}
                      {entry.actor && entry.actor !== 'Benchmark CRM' && (
                        <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 5 }}>· {entry.actor}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {fmtTimeAgoFeed(entry.timestamp)}
                    </div>
                  </div>

                  {/* Preview */}
                  {preview && (
                    <div style={{
                      fontSize: 12, color: 'var(--muted)', marginTop: 3,
                      whiteSpace: entry.type === 'sms' ? 'normal' : 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      lineHeight: 1.5,
                      background: entry.type === 'note' ? 'rgba(230,168,23,0.06)' : 'transparent',
                      padding: entry.type === 'note' ? '4px 8px' : '0',
                      borderRadius: entry.type === 'note' ? 6 : 0,
                      border: entry.type === 'note' ? '1px solid rgba(230,168,23,0.15)' : 'none',
                    }}>
                      {preview.length > 200 ? preview.substring(0, 200) + '...' : preview}
                    </div>
                  )}

                  {/* Extra changes */}
                  {entry.changes?.length > 1 && entry.type !== 'sms' && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      {entry.changes.slice(1).map((c, ci) => c && (
                        <span key={ci} style={{ fontSize: 10, color: 'var(--muted)', background: 'var(--surface2)', padding: '1px 6px', borderRadius: 4, border: '1px solid var(--border)' }}>
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {filtered.length > limit && (
            <button
              onClick={() => setLimit(l => l + 20)}
              className="btn btn-secondary btn-sm"
              style={{ alignSelf: 'center', marginTop: 8, fontSize: 11 }}
            >
              Load {Math.min(20, filtered.length - limit)} more ({filtered.length - limit} remaining)
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── QuickDropdown ─────────────────────────────────────────────────────────────
function QuickDropdown({ triggerLabel, triggerStyle, items }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const closeTimer = useRef(null)
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])
  const handleMouseLeave = () => { closeTimer.current = setTimeout(() => setOpen(false), 300) }
  const handleMouseEnter = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true) }
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button className="quick-btn" style={triggerStyle} onClick={() => setOpen(o => !o)}>{triggerLabel}</button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 300, background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 210, marginTop: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {items.map(({ label, action, disabled }) => (
            <button key={label} disabled={disabled} onClick={() => { if (!disabled) { action(); setOpen(false) } }} style={{ background: 'none', border: 'none', borderBottom: '1px solid var(--border)', padding: '11px 16px', textAlign: 'left', fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'var(--font)', color: disabled ? 'var(--muted)' : 'var(--text)', fontWeight: 500, opacity: disabled ? 0.6 : 1 }} onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--surface2)' }} onMouseLeave={e => e.currentTarget.style.background = 'none'}>{label}</button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── InlineRenewalEdit ─────────────────────────────────────────────────────────
function InlineRenewalEdit({ value, onSave, fmtDate }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value || '')
  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="date" className="form-input" style={{ padding: '3px 8px', fontSize: 12, height: 28, width: 140 }} value={val} onChange={e => setVal(e.target.value)} autoFocus />
        <button className="btn btn-primary btn-sm" style={{ padding: '3px 10px', fontSize: 11 }} onClick={() => { onSave(val); setEditing(false) }}>Save</button>
        <button className="btn btn-secondary btn-sm" style={{ padding: '3px 10px', fontSize: 11 }} onClick={() => setEditing(false)}>✕</button>
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} onClick={() => setEditing(true)} title="Click to edit renewal date">
      <span style={{ fontSize: 13, fontWeight: 600 }}>{value ? fmtDate(value) : '—'}</span>
      <span style={{ fontSize: 10, color: 'var(--muted)' }}>✏️</span>
    </div>
  )
}

// NamePromptModal removed — fact finds now auto-named by type + date

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
// ── NEW DOCS TAB COMPONENT ──────────────────────────────────────────────────────
function DocsTabNew({ clientId, clientName, documentsWithNames, coverConfirmations, onFilesSelected, loadAll, showToast, userName }) {
  const [newDocuments, setNewDocuments] = useState([])
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedConfirmation, setSelectedConfirmation] = useState(null)
  const [docType, setDocType] = useState('client_correspondence')
  const [showArchive, setShowArchive] = useState(false)

  useEffect(() => {
    const loadNewDocs = async () => {
      try {
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false })
        if (error) throw error
        setNewDocuments(data || [])
      } catch (err) {
        console.error('Load docs:', err)
      }
    }
    loadNewDocs()
  }, [clientId])

  const handleFilesDropped = async (files) => {
    if (!selectedConfirmation) { showToast('⚠️ Select confirmation first'); return }
    onFilesSelected(files)
    
    try {
      for (const file of files) {
        const storagePath = buildDocumentPath(clientId, file.name)
        const { error: uploadError } = await supabase.storage.from('documents').upload(storagePath, file, { upsert: true })
        if (uploadError) throw uploadError

        const { data: pubData } = supabase.storage.from('documents').getPublicUrl(storagePath)
        const { error: dbError } = await supabase.from('documents').insert({
          client_id: clientId,
          cover_confirmation_id: selectedConfirmation,
          document_type: docType,
          file_url: pubData.publicUrl,
          file_name: file.name,
          uploaded_by: userName,
        })
        if (dbError) throw dbError
      }
      
      const { data: freshDocs } = await supabase.from('documents').select('*').eq('client_id', clientId).order('created_at', { ascending: false })
      setNewDocuments(freshDocs || [])
      showToast(`${files.length} document(s) uploaded`)
      setShowUploadModal(false)
      setSelectedConfirmation(null)
      await loadAll()
    } catch (err) {
      showToast(`Upload failed: ${err.message}`)
    }
  }

  const DOC_TYPES = { bind_notes: '📝 Bind Notes', cover_confirmed: '✅ Cover Confirmed', funding_advice: '💰 Funding', client_correspondence: '📧 Correspondence', compliance_docs: '⚖️ Compliance', other: '📄 Other' }
  const categorisedDocs = newDocuments.filter(d => d.cover_confirmation_id)

  return (
    <div>
      <button className="btn btn-orange btn-sm" style={{ marginBottom: 16 }} onClick={() => { if (!coverConfirmations?.length) { showToast('Create confirmation first'); return } setShowUploadModal(true) }}>+ Upload</button>

      {showUploadModal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowUploadModal(false)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Upload Document</div><button className="modal-close" onClick={() => setShowUploadModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="full">
                  <label className="form-label">Select confirmation *</label>
                  <select className="form-select" value={selectedConfirmation || ''} onChange={e => setSelectedConfirmation(e.target.value || null)}>
                    <option value="">— Select —</option>
                    {coverConfirmations?.map(cc => <option key={cc.id} value={cc.id}>{cc.title}</option>)}
                  </select>
                </div>
                <div className="full">
                  <label className="form-label">Type</label>
                  <select className="form-select" value={docType} onChange={e => setDocType(e.target.value)}>
                    {Object.entries(DOC_TYPES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 16, marginTop: 16, border: '2px dashed var(--forest-border)', borderRadius: 12, padding: '20px', textAlign: 'center', cursor: 'pointer', background: 'rgba(30,61,48,0.02)' }} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); handleFilesDropped(Array.from(e.dataTransfer.files)) }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📁</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--forest)' }}>Drop files here</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Drag & drop to upload</div>
              </div>
              <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => setShowUploadModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {categorisedDocs.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>📋 New Documents</div>
          {categorisedDocs.map(doc => (
            <div key={doc.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ fontSize: 14 }}>📄</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--forest)' }}>{DOC_TYPES[doc.document_type] || doc.document_type}</div>
                <div style={{ fontSize: 11, color: 'var(--text)' }}>{doc.file_name}</div>
                <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 2 }}>{new Date(doc.created_at).toLocaleDateString('en-AU')} · {doc.uploaded_by}</div>
              </div>
              <a className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} href={doc.file_url} target="_blank" rel="noreferrer">Open</a>
            </div>
          ))}
        </div>
      )}

      {documentsWithNames && documentsWithNames.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: '#888' }} onClick={() => setShowArchive(!showArchive)}>
            {showArchive ? '▼' : '▶'} 📦 Archive ({documentsWithNames.length})
          </button>
          {showArchive && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {documentsWithNames.map((doc, idx) => (
                <div key={idx} style={{ background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, opacity: 0.7 }}>
                  <div>📋</div>
                  <div style={{ flex: 1, fontSize: 11 }}>{doc.name}</div>
                  <a className="btn btn-secondary btn-sm" style={{ fontSize: 9 }} href={doc.url} target="_blank" rel="noreferrer">Open</a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {newDocuments.length === 0 && documentsWithNames?.length === 0 && (
        <div className="empty" style={{ padding: '40px 24px' }}>
          <div className="empty-icon">📁</div>
          <div className="empty-title">No documents</div>
          <div className="empty-sub">Drag & drop to add documents</div>
        </div>
      )}
    </div>
  )
}
export function ClientDetailModal({ client, calls, texts, loadAll, onClose, showToast, fmtDate, user }) {
  // ── Fact find state (multi-instance) ──────────────────────────────────────
  const [activeFfId, setActiveFfId] = useState(null)
  const [activeFfType, setActiveFfType] = useState(null)
  const [ffData, setFfData] = useState({})
  const [ffIncompleteKeys, setFfIncompleteKeys] = useState(new Set())
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false)
  const [ffNamePrompt, setFfNamePrompt] = useState(null) // { type: 'Car' } when prompting
  const [showPostBindWarning, setShowPostBindWarning] = useState(null) // ff awaiting acknowledgement

  // ── General state ─────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false)
  const [clientData, setClientData] = useState(client)
  const [editingInfo, setEditingInfo] = useState(false)
  const [infoForm, setInfoForm] = useState({})
  const [activeTab, setActiveTab] = useState(() => getTabMemory(client.id))
  const [auditFilter, setAuditFilter] = useState('all')
  const [auditSearch, setAuditSearch] = useState('')
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)
  const [showSms, setShowSms] = useState(false)
  const [smsInitialTpl, setSmsInitialTpl] = useState(null)
  const [sendingSms, setSendingSms] = useState(false)
  const [statusReasonModal, setStatusReasonModal] = useState(null)
  const [policyModal, setPolicyModal] = useState(false)
  const [policyForm, setPolicyForm] = useState({ policy_type: '', start_date: '', renewal_date: '' })
  const [savingPolicy, setSavingPolicy] = useState(false)
  const [showQuotePack, setShowQuotePack] = useState(false)
 const [showCoverConfirmed, setShowCoverConfirmed] = useState(false)
  const [showTowerLifeReferral, setShowTowerLifeReferral] = useState(false)
  const [towerLifeNote, setTowerLifeNote] = useState('')
  const [sendingTowerLife, setSendingTowerLife] = useState(false)
  const [showCallScript, setShowCallScript] = useState(false)
  const [callScriptInitialTab, setCallScriptInitialTab] = useState('opening')
  const [viewingFf, setViewingFf] = useState(null)
const [analysingFf, setAnalysingFf] = useState(null)   // ff being analysed
const [ffAnalysis, setFfAnalysis] = useState(null)      // analysis result text
const [analysisLoading, setAnalysisLoading] = useState(false)
  const [savedQuotePacks, setSavedQuotePacks] = useState([])
  const [savedCoverConfirmeds, setSavedCoverConfirmeds] = useState([])
  const [loaSubmissions, setLoaSubmissions] = useState([])
  const [emailAuditEntries, setEmailAuditEntries] = useState([])
  const [clientDocuments, setClientDocuments] = useState([])
  const [loadingArtifacts, setLoadingArtifacts] = useState(false)
  const [uploadingDocuments, setUploadingDocuments] = useState(false)
  const [sendingRequiredInfo, setSendingRequiredInfo] = useState(false)
  const [showRequiredInfoOverride, setShowRequiredInfoOverride] = useState(false)
  const [showRequiredInfoConfirm, setShowRequiredInfoConfirm] = useState(false)
  const [requiredInfoVariantOverride, setRequiredInfoVariantOverride] = useState(null)
  const [sendConsentSms, setSendConsentSms] = useState(false)
  const [callSearch, setCallSearch] = useState('')
  const [compareWith, setCompareWith] = useState(null)
  const [syncingCalls, setSyncingCalls] = useState(false)
  const [quoteStatusFilter, setQuoteStatusFilter] = useState('all')
  const textsEndRef = useRef(null)

  const handleTabChange = (tab) => { setActiveTab(tab); setTabMemory(client.id, tab) }

  // ── Load client ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadClient = async () => {
      const { data } = await supabase.from('clients').select('*').eq('id', client.id).single()
      if (data) setClientData(data)
    }
if (client.id && !showCoverConfirmed && !showQuotePack && !editingInfo && !activeFfType) loadClient()
  }, [client.id, showCoverConfirmed, showQuotePack, editingInfo])

 useEffect(() => {
    const ffMem = getFfMemory(client.id)
    if (ffMem && !activeFfId) {
      const { ffId, ffType } = ffMem
      const draft = loadDraft(client.id, ffId)
      if (draft && Object.keys(draft).length > 0) {
        setActiveFfId(ffId)
        setActiveFfType(ffType)
        setFfData(draft)
      }
    }
  }, [client.id])

  useEffect(() => { requestPushPermission() }, [])

  // ── Realtime texts ───────────────────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase
      .channel(`texts-${client.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'texts' }, (payload) => {
        const t = payload.new
        const normPhone = normaliseAuPhone(client.phone || '')
        const isForThisClient = t.client_id === client.id || t.from_number === normPhone || t.to_number === normPhone
        if (isForThisClient && t.direction === 'inbound') {
          sendPushNotification(`📱 New text from ${clientData.company_name}`, t.body?.substring(0, 100) || 'New message received', () => handleTabChange('texts'))
        }
        loadAll()
      })
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [client.id, client.phone, clientData.company_name, loadAll])

  useEffect(() => {
    if (activeTab === 'texts') setTimeout(() => textsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }, [activeTab, texts])

  // ── Load artifacts ───────────────────────────────────────────────────────────
  useEffect(() => {
    const loadArtifacts = async () => {
      if (!client.id) return
      setLoadingArtifacts(true)
      const [quoteResult, coverResult, loaResult] = await Promise.all([
        supabase.from('quote_packs').select('*').eq('client_id', client.id).order('created_at', { ascending: false }),
        supabase.from('cover_confirmations').select('*').eq('client_id', client.id).order('created_at', { ascending: false }),
        supabase.from('appointments').select('*').eq('crm_ref', client.id).order('submitted_at', { ascending: false }),
      ])
      setSavedQuotePacks(quoteResult.data || [])
      setSavedCoverConfirmeds(coverResult.data || [])
      setLoaSubmissions(loaResult.data || [])
      setEmailAuditEntries([])
      try {
        const prefix = `${DOCUMENT_PREFIX}/${client.id}`
        const { data, error } = await supabase.storage.from('documents').list(prefix, { limit: 100, sortBy: { column: 'name', order: 'desc' } })
        if (error) throw error
        const documents = (data || []).filter(i => i.name).map(item => {
          const path = `${prefix}/${item.name}`
          const { data: pub } = supabase.storage.from('documents').getPublicUrl(path)
          const displayName = item.name.replace(/^\d+-/, '')
          return { name: item.name, displayName, path, url: pub.publicUrl, created_at: item.created_at || item.updated_at || null, metadata: item.metadata }
        })
        setClientDocuments(documents)
      } catch { setClientDocuments([]) }
      setLoadingArtifacts(false)
    }
    loadArtifacts()
  }, [client.id, showQuotePack, showCoverConfirmed])

const [draftSavedAt, setDraftSavedAt] = useState(null)
  useEffect(() => {
    if (activeFfId && Object.keys(ffData).length > 0) {
      saveDraft(client.id, activeFfId, ffData)
      setDraftSavedAt(Date.now())
    }
  }, [ffData, activeFfId, client.id])

  // ── Derived data ─────────────────────────────────────────────────────────────
  const factFinds = clientData.fact_finds || []
  const normalisedPhone = normaliseAuPhone(clientData.phone || '')

  const clientTexts = useMemo(() => (texts || [])
    .filter(t => t.client_id === clientData.id || t.from_number === normalisedPhone || t.to_number === normalisedPhone)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    [texts, clientData.id, normalisedPhone]
  )

  const clientCalls = useMemo(() => (calls || [])
    .filter(call => normaliseAuPhone(call.contact_number || '') === normalisedPhone)
    .sort((a, b) => new Date(b.started_at || 0) - new Date(a.started_at || 0)),
    [calls, normalisedPhone]
  )

  const filteredCalls = useMemo(() =>
    !callSearch.trim() ? clientCalls : clientCalls.filter(c => (c.transcript || '').toLowerCase().includes(callSearch.toLowerCase())),
    [clientCalls, callSearch]
  )

  const complianceState = useMemo(
    () => buildComplianceState(clientData, activeFfType ? { policy_type: activeFfType } : {}),
    [clientData, activeFfType]
  )

  const daysSinceContact = useMemo(() => {
    const events = [
      ...clientCalls.map(c => c.started_at),
      ...clientTexts.map(t => t.created_at),
      ...(clientData.audit_log || []).filter(e => ['note','sms','call','email','contacted'].includes(e.type)).map(e => e.timestamp),
    ].filter(Boolean).map(d => new Date(d)).filter(d => !isNaN(d))
    if (!events.length) return null
    const latest = new Date(Math.max(...events))
    return Math.floor((new Date() - latest) / 86400000)
  }, [clientCalls, clientTexts, clientData.audit_log])

  // ── Audit events ──────────────────────────────────────────────────────────────
  const auditEvents = useMemo(() => {
    const events = []
    ;(clientData.audit_log || []).forEach((entry, i) => events.push({
      key: `audit-${i}-${entry.timestamp || i}`, type: entry.type || 'audit',
      timestamp: entry.timestamp || clientData.created_at || new Date().toISOString(),
      title: entry.action || 'Audit update', actor: entry.broker || 'Benchmark CRM', changes: entry.changes || [],
    }))
    ;(clientData.activity_log || []).filter(e => e.type === 'note').forEach((entry, i) => events.push({
      key: `note-${i}-${entry.timestamp || i}`, type: 'note',
      timestamp: entry.timestamp || clientData.created_at || new Date().toISOString(),
      title: 'Note added', actor: entry.text || 'Note', changes: [],
    }))
    ;(savedQuotePacks || []).forEach(r => events.push({
      key: `quote-${r.id}`, type: 'quote', timestamp: r.created_at || new Date().toISOString(),
      title: r.status === 'Sent' ? 'Quote pack sent' : 'Quote pack saved',
      actor: r.created_by || 'Benchmark CRM', changes: [r.title || 'Quote Pack', `Status: ${r.status || 'Draft'}`],
    }))
    ;(savedCoverConfirmeds || []).forEach(r => events.push({
      key: `cover-${r.id}`, type: 'coverconfirmed', timestamp: r.created_at || new Date().toISOString(),
      title: r.status === 'Sent' ? 'Cover confirmed sent' : 'Cover confirmed saved',
      actor: r.created_by || 'Benchmark CRM', changes: [r.title || 'Cover Confirmed', `Status: ${r.status || 'Draft'}`],
    }))
    clientTexts.forEach(t => events.push({
      key: `text-${t.id}`, type: 'sms', timestamp: t.created_at || new Date().toISOString(),
      title: t.direction === 'outbound' ? 'SMS sent' : 'SMS received',
      actor: t.broker_name || 'SMS', changes: [t.body || ''],
    }))
    clientCalls.forEach(c => events.push({
      key: `call-${c.id}`, type: 'call', timestamp: c.started_at || new Date().toISOString(),
      title: c.direction === 'inbound' ? 'Inbound call' : 'Outbound call',
      actor: c.broker_name || 'Phone system',
      changes: [c.duration ? fmtDuration(c.duration) : null, c.transcript ? 'Transcript available' : null].filter(Boolean),
    }))
    ;(loaSubmissions || []).forEach(s => events.push({
      key: `loa-${s.id}`, type: 'loa', timestamp: s.submitted_at || new Date().toISOString(),
      title: 'LOA submitted', actor: s.full_name || 'LOA',
      changes: [s.company_name || '', s.policies || 'All classes'].filter(Boolean),
    }))
    return events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  }, [clientCalls, clientData, clientTexts, loaSubmissions, savedCoverConfirmeds, savedQuotePacks])

  const filteredAuditEvents = useMemo(() => {
    let events = auditFilter === 'all' ? auditEvents : auditEvents.filter(e => e.type === auditFilter)
    if (auditSearch.trim()) {
      const q = auditSearch.toLowerCase()
      events = events.filter(e =>
        e.title?.toLowerCase().includes(q) ||
        e.actor?.toLowerCase().includes(q) ||
        e.changes?.some(c => c?.toLowerCase().includes(q))
      )
    }
    return events
  }, [auditEvents, auditFilter, auditSearch])

  const sentQuotePacks = savedQuotePacks.filter(q => q.status === 'Sent').length
  const sentCoverConfirmeds = savedCoverConfirmeds.filter(c => c.status === 'Sent').length

  const filteredQuotePacks = useMemo(() => {
    if (quoteStatusFilter === 'all') return savedQuotePacks
    return savedQuotePacks.filter(q => (q.status || 'Draft').toLowerCase() === quoteStatusFilter)
  }, [savedQuotePacks, quoteStatusFilter])

  // ── Fact find completion ────────────────────────────────────────────────────
  const getCompletionPct = (ff) => {
    const template = FACT_FINDS[ff.type]
    if (!template) return 0
    const data = ff.data || {}
    let total = 0; let done = 0
template.sections.forEach(s => (s.fields || []).forEach(field => {
      if (!isCountableFfField(field)) return
      total += 1
      const v = data[field.key]
      if (v && v !== '' && !(Array.isArray(v) && v.length === 0)) done += 1
    }))
    return total > 0 ? Math.round((done / total) * 100) : 0
  }

  const getIncompleteFields = (ffType, data) => {
    const template = FACT_FINDS[ffType]
    if (!template) return new Set()
    const incomplete = new Set()
template.sections.forEach(s => (s.fields || []).forEach(field => {
      if (!isCountableFfField(field)) return
      const v = data[field.key]
      if (!v || v === '' || (Array.isArray(v) && v.length === 0)) incomplete.add(field.key)
    }))
    return incomplete
  }

  // ── Core write function ───────────────────────────────────────────────────────
  const writeClientHistory = async ({ auditEntry, activityEntry, extraUpdates = {} }) => {
    const updatedAudit = auditEntry ? [auditEntry, ...(clientData.audit_log || [])] : (clientData.audit_log || [])
    const updatedActivity = activityEntry ? [activityEntry, ...(clientData.activity_log || [])] : (clientData.activity_log || [])
    const nextClient = { ...clientData, ...extraUpdates, audit_log: updatedAudit, activity_log: updatedActivity }
    await supabase.from('clients').update({ ...extraUpdates, audit_log: updatedAudit, activity_log: updatedActivity }).eq('id', clientData.id)
    setClientData(nextClient)
    return nextClient
  }

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const addNote = async () => {
    if (!newNote.trim()) return
    setAddingNote(true)
    await writeClientHistory({
      auditEntry: makeAuditEntry('note', 'Note added', user.name, [newNote.trim()]),
      activityEntry: makeActivityEntry('note', newNote.trim(), 'Note'),
    })
    setNewNote(''); setAddingNote(false); await loadAll(); showToast('Note added')
  }

  const markAsContacted = async () => {
    await writeClientHistory({
      auditEntry: makeAuditEntry('contacted', `Contacted by ${user.name}`, user.name, ['Contact attempt logged']),
      activityEntry: makeActivityEntry('contacted', `Contacted by ${user.name}`, '📲'),
    })
    await loadAll()
    showToast('📲 Contact logged')
  }

  const saveRenewalDate = async (newDate) => {
    if (!newDate) return
    await writeClientHistory({
      auditEntry: makeAuditEntry('edit', 'Renewal date updated', user.name, [`Renewal: ${newDate}`]),
      activityEntry: makeActivityEntry('edit', `Renewal date updated to ${newDate} by ${user.name}`, '✏️'),
      extraUpdates: { renewal_date: newDate },
    })
    await loadAll()
    showToast('Renewal date updated')
  }

  // ── Fact find handlers (multi-instance) ───────────────────────────────────────
  const startFactFindWithName = (type, label) => {
    const ffId = generateFfId(type)
    const savedFf = null // New instance — no existing data
    const baseData = {
      ff_company_name: clientData.company_name || '',
      ff_contact_name: clientData.contact_name || '',
      ff_phone: clientData.phone || '',
      ff_email: clientData.email || '',
      ff_business_address: clientData.address || '',
      ff_residential_address: clientData.address || '',
      ff_property_address: clientData.address || '',
      ff_mailing_address: clientData.address || '',
      ff_insured_name: clientData.company_name || '',
      ff_dob: clientData.dob || '',
    }
    const draft = loadDraft(client.id, ffId)
    setActiveFfId(ffId)
    setActiveFfType(type)
    setFfData(draft || baseData)
    setFfIncompleteKeys(new Set())
    setShowIncompleteWarning(false)
    setFfNamePrompt(null)
    // Store the label/id mapping in the fact find data itself
    setFfData(prev => ({ ...prev, _ff_id: ffId, _ff_label: label }))
    setFfMemory(client.id, ffId, type)
  }

const startFactFind = (type) => {
const defaultLabel = `${type} — ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
    startFactFindWithName(type, defaultLabel)
  }

const editFactFind = (ff) => {
    const ffId = ff.id || ff._ff_id || `${ff.type}-legacy`
    const isBound = savedCoverConfirmeds.some(cc =>
      cc.fact_find_id === ffId || cc.fact_find_id === (ff.id || ff._ff_id)
    )
    if (isBound) { setShowPostBindWarning(ff); return }
    doEditFactFind(ff)
  }

const doEditFactFind = (ff) => {
    const ffId = ff.id || ff._ff_id || `${ff.type}-legacy`
    const draft = loadDraft(client.id, ffId)
    const base = ff.data || {}
    const merged = draft ? { ...base, ...draft } : base
    setActiveFfId(ffId)
    setActiveFfType(ff.type)
    setFfData(merged)
    setFfIncompleteKeys(new Set())
    setShowIncompleteWarning(false)
    setFfNamePrompt(null)
    setShowPostBindWarning(null)
  }

const analyseFactFind = async (ff) => {
    setAnalysingFf(ff)
    setFfAnalysis(null)
    setAnalysisLoading(true)
    const template = FACT_FINDS[ff.type]
    const data = ff.data || {}
    // Build a readable version of answers (skip empty fields)
    const readable = {}
    template?.sections?.forEach(section => {
      section.fields?.forEach(field => {
        const val = data[field.key]
        if (val && val !== '' && !(Array.isArray(val) && val.length === 0)) {
          readable[field.label] = Array.isArray(val) ? val.join(', ') : val
        }
      })
    })
    try {
const { data: { session } } = await supabase.auth.getSession()
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ai-analyse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ prompt: `You are a senior insurance broker at Benchmark Insurance Brokers in Australia. Review the following ${ff.type} fact find answers for ${clientData.company_name} and provide a concise analysis under these headings:

**GAPS IN COVER** — based on their answers, what coverage risks are they exposed to that aren't addressed?
**MISSING INFORMATION** — what answers are vague, incomplete or missing that we need before quoting?
**UNDERINSURANCE RISKS** — are any sum insured amounts, limits or values likely to be inadequate?
**FOLLOW-UP QUESTIONS** — list 3–5 specific questions to ask on the next call.
**BROKER SUMMARY** — 2–3 sentences suitable for internal bind notes.

Be specific to their situation. No generic advice.

Fact find answers:
${JSON.stringify(readable, null, 2)}` })
      })
      const result = await response.json()
      setFfAnalysis(result.text || 'No analysis returned.')
    } catch(e) {
      setFfAnalysis('Analysis failed — check your connection and try again.')
    }
    setAnalysisLoading(false)
  }

const cancelFactFind = () => {
    clearFfMemory(client.id)
    setActiveFfId(null); setActiveFfType(null); setFfData({}); setFfIncompleteKeys(new Set()); setShowIncompleteWarning(false); setCompareWith(null)
  }

const persistFactFind = async ({ closeAfter = true, toastText = 'Fact find saved' } = {}) => {
    setSaving(true)
    // Always fetch fresh fact_finds from DB before saving to avoid stale overwrites
    const { data: freshClient } = await supabase.from('clients').select('fact_finds').eq('id', clientData.id).single()
    const currentFactFinds = freshClient?.fact_finds || clientData.fact_finds || []
    const ffId = activeFfId || generateFfId(activeFfType)
    const generateSmartLabel = (type, data) => {
      // Car / Motor
      if (['Car','Commercial Motor','Truck','Motorbike','Caravan','Boat'].includes(type)) {
        const year = data.ff_vehicle_year || data.ff_year || ''
        const make = data.ff_vehicle_make || data.ff_make || ''
        const model = data.ff_vehicle_model || data.ff_model || ''
        const rego = data.ff_rego || data.ff_vehicle_rego || ''
        if (year || make || model) return [year, make, model].filter(Boolean).join(' ')
        if (rego) return `Rego ${rego}`
      }
      // Property / Landlord / Home
      if (['Commercial Property','Landlord','Home & Contents','Farm'].includes(type)) {
        const addr = data.ff_property_address || data.ff_risk_address || data.ff_address || data.ff_business_address || ''
        if (addr) return addr.split(',')[0].trim()
      }
      // Public Liability / Business
      if (['Public Liability','Professional Indemnity','Management Liability','Cyber','Workers Compensation','Business Package'].includes(type)) {
        const biz = data.ff_business_name || data.ff_company_name || data.ff_insured_name || ''
        if (biz) return biz
      }
      // Plant & Equipment / Construction
      if (['Plant & Equipment','Construction','Annual Works','Single Project','Owner Builder'].includes(type)) {
        const item = data.ff_plant_description || data.ff_equipment_description || data.ff_project_name || data.ff_project_address || ''
        if (item) return item.split(',')[0].trim()
      }
      // Carriers / Marine
      if (['Carriers','Marine'].includes(type)) {
        const vessel = data.ff_vessel_name || data.ff_fleet_description || data.ff_cargo_description || ''
        if (vessel) return vessel.split(',')[0].trim()
      }
      // Fallback — use existing label or type + date
      return null
    }
    const smartLabel = generateSmartLabel(activeFfType, ffData)
    const ffLabel = smartLabel || ffData._ff_label || `${activeFfType} — ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
    const existingIndex = currentFactFinds.findIndex(ff => (ff.id || ff._ff_id) === ffId)

    const ffEntry = {
      id: ffId,
      _ff_id: ffId,
      _ff_label: ffLabel,
      type: activeFfType,
      data: ffData,
      updated_at: new Date().toISOString(),
      created_at: existingIndex >= 0 ? factFinds[existingIndex].created_at : new Date().toISOString(),
    }

const updatedFactFinds = existingIndex >= 0
      ? currentFactFinds.map((ff, i) => i === existingIndex ? ffEntry : ff)
      : [...currentFactFinds, ffEntry]

const policyTypeUpdate = clientData.policy_type ? {} : { policy_type: activeFfType }
    const emailUpdate = ffData.ff_email && !clientData.email ? { email: ffData.ff_email } : {}
    const updatedAudit = [makeAuditEntry('factfind', `Fact find saved: ${ffLabel} (${activeFfType})`, user.name), ...(clientData.audit_log || [])]
    const updatedActivity = [makeActivityEntry('factfind', `Fact find updated: ${ffLabel} by ${user.name}`, 'Fact Find'), ...(clientData.activity_log || [])]
    const savePayload = { fact_finds: updatedFactFinds, policy_type: clientData.policy_type || activeFfType, ...policyTypeUpdate, ...emailUpdate, audit_log: updatedAudit, activity_log: updatedActivity }
    const { error: saveError } = await supabase.from('clients').update(savePayload).eq('id', clientData.id)
    if (saveError) { console.error('Fact find save error:', saveError); showToast(`Save failed: ${saveError.message}`); setSaving(false); return clientData }
    const { data: freshAfterSave } = await supabase.from('clients').select('*').eq('id', clientData.id).single()
    if (freshAfterSave) setClientData(freshAfterSave)
    const nextClient = freshAfterSave || { ...clientData, ...savePayload }
    clearDraft(client.id, ffId)
    await loadAll()
    setSaving(false)
    if (closeAfter) { clearFfMemory(client.id); setActiveFfId(null); setActiveFfType(null); setFfData({}); setFfIncompleteKeys(new Set()); setShowIncompleteWarning(false); setCompareWith(null) }
    if (toastText) showToast(toastText)
    return nextClient
  }

const saveFactFind = async () => {
    const incomplete = getIncompleteFields(activeFfType, ffData)
    setFfIncompleteKeys(incomplete)
    if (incomplete.size > 0) {
      setShowIncompleteWarning(true)
      showToast(`${incomplete.size} field${incomplete.size > 1 ? 's' : ''} still needed — saving anyway`)
      // Don't return — save with incomplete fields highlighted
    } else {
      setShowIncompleteWarning(false)
    }
    await persistFactFind()
  }



  // ── Edit client info ──────────────────────────────────────────────────────────
const saveInfo = async () => {
    setSaving(true)
    const normalisedForm = { ...infoForm, phone: infoForm.phone ? normaliseAuPhone(infoForm.phone) : null }
    const changes = Object.keys(infoForm).filter(k => (infoForm[k] || '') !== (clientData[k] || '')).map(k => `${k}: "${clientData[k] || '-'}" → "${infoForm[k] || '-'}"`)
    const updatedAudit = [makeAuditEntry('edit', 'Client details updated', user.name, changes), ...(clientData.audit_log || [])]
    const updatedActivity = [makeActivityEntry('edit', `Client details updated by ${user.name}`, 'Edit'), ...(clientData.activity_log || [])]
const { company_name, phone, email, address, contact_name, referring_partner, sub_referrer, assigned_broker, notes, dob } = normalisedForm
    const { error: updateError } = await supabase.from('clients').update({ company_name, phone, email, address, contact_name, referring_partner, sub_referrer, assigned_broker, notes, dob: dob || null, audit_log: updatedAudit, activity_log: updatedActivity }).eq('id', clientData.id)
    if (updateError) { console.error('Supabase update error:', updateError); showToast(`Save failed: ${updateError.message}`); setSaving(false); return }
    const { data: fresh } = await supabase.from('clients').select('*').eq('id', clientData.id).single()
    if (fresh) setClientData(fresh)
    await loadAll(); setSaving(false); setEditingInfo(false); showToast('Details updated')
  }
  // ── Status change ─────────────────────────────────────────────────────────────
  const handleStatusChange = (newStatus) => {
    if (newStatus === 'Policy Placed') {
      const missingSteps = []
      if (!complianceState.important_info_sent_at) missingSteps.push('Required info not sent')
      if (!complianceState.tmd_checked_at) missingSteps.push('TMD not checked')
      if (complianceState.is_claims_made && !complianceState.claims_made_notice_at) missingSteps.push('Claims-made notice not given')
      if (complianceState.informed_consent_required && !complianceState.informed_consent_received_at) missingSteps.push('Informed consent not recorded')
      if (missingSteps.length > 0) {
        const proceed = window.confirm(`⚠️ Compliance override\n\nThe following steps are not recorded in the CRM:\n\n${missingSteps.map(s => `• ${s}`).join('\n')}\n\nIf these were completed outside the CRM (e.g. verbally or via email), click OK to override and place cover.\n\nClick Cancel to go back.`)
        if (!proceed) return
      }
      const oneYearOut = new Date(); oneYearOut.setFullYear(oneYearOut.getFullYear() + 1)
      setPolicyForm({ policy_type: clientData.policy_type || factFinds[0]?.type || '', start_date: new Date().toISOString().split('T')[0], renewal_date: oneYearOut.toISOString().split('T')[0] })
      setPolicyModal(true); return
    }
    if (REASON_REQUIRED_STATUSES.includes(newStatus)) { setStatusReasonModal(newStatus); return }
    doStatusChange(newStatus, null)
  }

  const doStatusChange = async (newStatus, reason) => {
    setStatusReasonModal(null)
    const changes = [`Status: "${clientData.lead_status || '-'}" → "${newStatus}"`]
    if (reason) changes.push(`Reason: "${reason}"`)
    await writeClientHistory({
      auditEntry: makeAuditEntry('status', `Status changed to ${newStatus}`, user.name, changes),
      activityEntry: makeActivityEntry('status', `Status changed to ${newStatus} by ${user.name}${reason ? ` — ${reason}` : ''}`, 'Status'),
      extraUpdates: { lead_status: newStatus, status_reason: reason || null },
    })
    if (SMS_TRIGGER_STATUSES.includes(newStatus) && clientData.phone) { setSmsInitialTpl(newStatus); setShowSms(true) }
    await loadAll()
  }

  const confirmPolicyPlaced = async () => {
    if (!policyForm.renewal_date) return showToast('Renewal date is required')
    setSavingPolicy(true)
    await writeClientHistory({
      auditEntry: makeAuditEntry('status', 'Policy placed', user.name, [`Renewal: ${policyForm.renewal_date}`]),
      activityEntry: makeActivityEntry('status', `Policy placed by ${user.name}`, 'Placed'),
     extraUpdates: { is_client: true, lead_status: 'Policy Placed', policy_type: [clientData.policy_type, policyForm.policy_type].filter(Boolean).filter((v,i,a) => a.indexOf(v) === i).join(', ') || null, policy_start_date: policyForm.start_date || null, renewal_date: policyForm.renewal_date },
    })
await loadAll(); setSavingPolicy(false); setPolicyModal(false); showToast(`${clientData.company_name} — policy placed`)
  }

  // ── SMS ───────────────────────────────────────────────────────────────────────
const handleSendSms = async (body, alsoEmail, emailSubject) => {
    if (!clientData.phone || !body.trim()) return showToast('Phone and message are required')
    setSendingSms(true)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const session = sessionData?.session
      if (!session) { showToast('Session expired — please refresh'); setSendingSms(false); return }
      const response = await fetch('https://dtdwmzviywqumpouqula.supabase.co/functions/v1/send-sms', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: normaliseAuPhone(clientData.phone), body: body.trim(), client_id: clientData.id, client_name: clientData.company_name, broker_name: user.name }),
      })
      const result = await response.json()
      if (!response.ok || !result.success) throw new Error(result.error || 'SMS failed')

      // Optionally also send a branded email (independent of SMS — email failure never fails the SMS)
      let emailSent = false
      if (alsoEmail && clientData.email) {
        try {
          const emailRes = await fetch('https://dtdwmzviywqumpouqula.supabase.co/functions/v1/send-client-email', {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: clientData.email.trim(),
              subject: emailSubject,
              bodyText: body.trim(),
              clientName: clientData.company_name,
              brokerName: user.name,
              client_id: clientData.id,
            }),
          })
          const emailResult = await emailRes.json()
          if (!emailRes.ok || !emailResult.success) throw new Error(emailResult.error || 'Email failed')
          emailSent = true
        } catch (emailErr) {
          showToast(`SMS sent, but email failed — ${emailErr.message}`)
        }
      }

      await writeClientHistory({
        auditEntry: makeAuditEntry(emailSent ? 'email' : 'sms', emailSent ? 'SMS + email sent to client' : 'SMS sent to client', user.name, [`To: ${clientData.phone}`, ...(emailSent ? [`Email: ${clientData.email}`] : []), body.trim().substring(0, 140)]),
        activityEntry: makeActivityEntry(emailSent ? 'email' : 'sms', `${emailSent ? 'SMS + email' : 'SMS'} sent by ${user.name}: "${body.trim().substring(0, 70)}"`, emailSent ? '📧' : 'SMS'),
      })
      await loadAll(); setShowSms(false); setSmsInitialTpl(null)
      showToast(emailSent ? 'SMS + email sent' : 'SMS sent')
    } catch (err) { showToast(err.message || 'SMS failed') }
    setSendingSms(false)
  }

  // ── Required Info Email ───────────────────────────────────────────────────────
  const sendRequiredInfoEmail = async (variantOverride = null) => {
    if (!clientData.email) return showToast('No email address on file for this client')
    setSendingRequiredInfo(true)
    try {
      let workingClient = clientData
      if (activeFfType) workingClient = await persistFactFind({ closeAfter: false, toastText: null })
      const currentState = buildComplianceState(workingClient, activeFfType ? { policy_type: activeFfType } : {})
      const snapshotUpdates = buildRequiredInfoCompletionUpdates(currentState, { method: 'CRM required info email', consentMethod: 'Sent by email' })
      const { data: { session } } = await supabase.auth.getSession()
      const response = await fetch('https://dtdwmzviywqumpouqula.supabase.co/functions/v1/send-required-info', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ client: workingClient, brokerName: user.name, brokerEmail: BROKER_EMAILS[user.name] || '', clientEmail: workingClient.email, complianceState: buildComplianceState(workingClient, snapshotUpdates), variantOverride }),
      })
      const result = await response.json()
      if (!response.ok || !result.success) throw new Error(result.error || 'Send failed')
const logResult = await logClientEmailAction({ supabase, client: workingClient, userName: user.name, emailType: 'Required Info', subject: result.subject || 'Important Information - Benchmark Insurance Brokers', recipient: workingClient.email, snapshotUpdates, activityText: `Required info email sent by ${user.name}` })
      setClientData(prev => ({ ...prev, audit_log: logResult.audit_log, activity_log: logResult.activity_log }))

      // Optionally fire commission consent SMS (retail only, broker can untick) — failure never blocks the email
      let consentSmsSent = false
      if (sendConsentSms && workingClient.phone) {
        try {
          const firstName = (workingClient.company_name || '').split(' ')[0] || 'there'
          const consentBody = `Hi ${firstName}, ${user.name.split(' ')[0]} from Benchmark here. Before we arrange your cover I need to confirm one thing: the insurer pays us a commission for setting up your policy. Are you happy for us to receive it? Reply YES to confirm — full details are in the information we've emailed you. Thanks!`
          const { data: { session: smsSession } } = await supabase.auth.getSession()
          const smsRes = await fetch('https://dtdwmzviywqumpouqula.supabase.co/functions/v1/send-sms', {
            method: 'POST',
            headers: { Authorization: `Bearer ${smsSession.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ to: normaliseAuPhone(workingClient.phone), body: consentBody, client_id: workingClient.id, client_name: workingClient.company_name, broker_name: user.name }),
          })
          const smsResult = await smsRes.json()
          if (smsRes.ok && smsResult.success) {
            consentSmsSent = true
            await writeClientHistory({
              auditEntry: makeAuditEntry('sms', 'Commission consent SMS sent', user.name, [`To: ${workingClient.phone}`, 'Awaiting client YES reply']),
              activityEntry: makeActivityEntry('sms', `Commission consent SMS sent to ${workingClient.company_name} by ${user.name}`, 'SMS'),
            })
          }
        } catch (smsErr) { /* SMS failure is non-blocking — email already sent */ }
      }

      await loadAll()
      showToast(consentSmsSent ? `Required info sent + consent SMS to ${workingClient.company_name}` : `Required info sent to ${workingClient.email}`)
    } catch (err) { showToast(`Send failed: ${err.message}`) }
    setSendingRequiredInfo(false)
  }

  // ── Sync Calls ────────────────────────────────────────────────────────────────
  const handleSyncCalls = async () => {
    setSyncingCalls(true); showToast('Syncing calls...')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('https://dtdwmzviywqumpouqula.supabase.co/functions/v1/sync-calls', { method: 'POST', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({}) })
      const result = await res.json()
      if (result.success) { await loadAll(); showToast(`✅ Synced ${result.stored} calls, transcribed ${result.transcribed}`) }
      else showToast('⚠️ Sync failed: ' + result.error)
    } catch { showToast('⚠️ Sync failed') }
    setSyncingCalls(false)
  }

  // ── Document handling ─────────────────────────────────────────────────────────
  const handleDocumentUpload = async (files) => {
    setUploadingDocuments(true)
    const uploaded = []
    try {
      for (const file of files) {
        const path = buildDocumentPath(clientData.id, file.name)
        const { error } = await supabase.storage.from('documents').upload(path, file, { upsert: true })
        if (!error) uploaded.push(file.name)
      }
      if (uploaded.length > 0) {
        await writeClientHistory({
          auditEntry: makeAuditEntry('document', uploaded.length === 1 ? 'Client document uploaded' : 'Client documents uploaded', user.name, uploaded),
          activityEntry: makeActivityEntry('document', `${uploaded.length} document(s) uploaded by ${user.name}`, 'Document'),
        })
      }
      const prefix = `${DOCUMENT_PREFIX}/${clientData.id}`
      const { data } = await supabase.storage.from('documents').list(prefix, { limit: 100, sortBy: { column: 'name', order: 'desc' } })
      const documents = (data || []).map(item => {
        const path = `${prefix}/${item.name}`
        const { data: pub } = supabase.storage.from('documents').getPublicUrl(path)
        const displayName = item.name.replace(/^\d+-/, '')
        return { name: item.name, displayName, path, url: pub.publicUrl, created_at: item.created_at || item.updated_at || null, metadata: item.metadata }
      })
      setClientDocuments(documents)
      await loadAll(); showToast(uploaded.length > 0 ? `${uploaded.length} document(s) uploaded` : 'No documents uploaded')
    } catch (err) { showToast(`Upload failed: ${err.message}`) }
    setUploadingDocuments(false)
  }

  const handleDocumentRename = async (doc, newName) => {
    const existingNames = clientData.document_names || {}
    const updatedNames = { ...existingNames, [doc.path]: newName }
    await supabase.from('clients').update({ document_names: updatedNames }).eq('id', clientData.id)
    setClientData(prev => ({ ...prev, document_names: updatedNames }))
    setClientDocuments(curr => curr.map(d => d.path === doc.path ? { ...d, displayName: newName } : d))
    showToast('Document renamed')
  }

  const openHtmlInWindow = (html, title) => {
    const w = window.open('', '_blank', 'width=1100,height=900')
    if (!w) return
    w.document.write(`<html><head><title>${title || 'Benchmark'}</title></head><body>${html}</body></html>`)
    w.document.close(); w.focus()
  }

  // ── Derived display values ────────────────────────────────────────────────────
  const isLead = clientData.lead_status && clientData.lead_status !== 'Policy Placed'
  const requiredInfoSent = !!complianceState.important_info_sent_at

  const tabs = [
    { key: 'overview', label: 'Overview', icon: '📊' },
    { key: 'factfinds', label: `Fact Finds${factFinds.length ? ` (${factFinds.length})` : ''}`, icon: '📋', highlight: true },
    { key: 'audit', label: 'Activity', icon: '📜' },
    { key: 'documents', label: `Docs${clientDocuments.length ? ` (${clientDocuments.length})` : ''}`, icon: '📁' },
    { key: 'calls', label: `Calls${clientCalls.length ? ` (${clientCalls.length})` : ''}`, icon: '📞' },
    { key: 'texts', label: `Texts${clientTexts.length ? ` (${clientTexts.length})` : ''}`, icon: '💬' },
    { key: 'quotes', label: `Quotes${savedQuotePacks.length ? ` (${sentQuotePacks > 0 ? `${sentQuotePacks} sent` : savedQuotePacks.length})` : ''}`, icon: '📦', highlight: true },
    { key: 'coverconfirmed', label: `Cover${savedCoverConfirmeds.length ? ` (${sentCoverConfirmeds > 0 ? `${sentCoverConfirmeds} sent` : savedCoverConfirmeds.length})` : ''}`, icon: '✅', highlight: true },
  ]

  const currentFfPct = activeFfType ? (() => {
    const template = FACT_FINDS[activeFfType]
    if (!template) return 0
    let total = 0; let done = 0
template.sections.forEach(s => (s.fields || []).forEach(field => {
      if (!isCountableFfField(field)) return
      total += 1
      const v = ffData[field.key]
      if (v && v !== '' && !(Array.isArray(v) && v.length === 0)) done += 1
    }))
    return total > 0 ? Math.round((done / total) * 100) : 0
  })() : 0

  const documentsWithNames = useMemo(() => {
    const names = clientData.document_names || {}
    return clientDocuments.map(doc => ({ ...doc, displayName: names[doc.path] || doc.displayName || doc.name }))
  }, [clientDocuments, clientData.document_names])

  const AUDIT_FILTER_TYPES = [
    { key: 'all', label: 'All' },
    { key: 'note', label: '📝 Notes' },
    { key: 'email', label: '📧 Emails' },
    { key: 'sms', label: '💬 SMS' },
    { key: 'call', label: '📞 Calls' },
    { key: 'status', label: '🔄 Status' },
    { key: 'contacted', label: '📲 Contacted' },
    { key: 'factfind', label: '📋 Fact Finds' },
    { key: 'document', label: '📄 Documents' },
  ]

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════════
  return (
    <div>
      {/* ── QUICK BAR ── */}
      {!activeFfType && !editingInfo && (
        <div className="quick-bar" style={{ flexWrap: 'wrap', gap: 6, padding: '8px 24px', backdropFilter: 'blur(8px)', background: 'rgba(255,255,255,0.85)' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Comms</span>
            <button className="quick-btn" onClick={() => { handleTabChange('audit'); setTimeout(() => document.querySelector('.note-textarea')?.focus(), 100) }}>📝 Note</button>
            <button className="quick-btn" style={{ borderColor: 'rgba(30,61,48,0.4)', color: 'var(--forest)', background: 'rgba(30,61,48,0.06)', fontWeight: 700 }} onClick={markAsContacted}>📲 Contacted</button>
            <button className={`quick-btn ${clientData.phone ? 'orange' : ''}`} onClick={() => { setSmsInitialTpl(null); setShowSms(true) }} disabled={!clientData.phone}>💬 SMS</button>
          </div>
          <div style={{ width: 1, height: 28, background: 'var(--border)', flexShrink: 0 }} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Scripts</span>
            <button className="quick-btn" style={{ borderColor: 'rgba(230,168,23,0.6)', color: '#7a5500', background: 'rgba(230,168,23,0.1)', fontWeight: 700 }} onClick={() => { setCallScriptInitialTab('opening'); setShowCallScript(true) }}>📋 Opening</button>
            <button className="quick-btn" style={{ borderColor: 'rgba(230,168,23,0.6)', color: '#7a5500', background: 'rgba(230,168,23,0.1)', fontWeight: 700 }} onClick={() => { setCallScriptInitialTab('closing'); setShowCallScript(true) }}>🔚 Closing</button>
          </div>
          <div style={{ width: 1, height: 28, background: 'var(--border)', flexShrink: 0 }} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Workflow</span>
            <button className="quick-btn" style={{ borderColor: requiredInfoSent ? 'rgba(30,61,48,0.4)' : 'rgba(30,61,48,0.3)', color: 'var(--forest)', background: requiredInfoSent ? 'rgba(30,61,48,0.1)' : 'rgba(30,61,48,0.06)', fontWeight: 700, opacity: (!clientData.email || sendingRequiredInfo) ? 0.5 : 1 }} disabled={!clientData.email || sendingRequiredInfo}
              onClick={() => {
                if (requiredInfoSent) { setShowRequiredInfoOverride(true); return }
                setRequiredInfoVariantOverride(null)
                setShowRequiredInfoConfirm(true)
              }}>
              {requiredInfoSent ? '✅' : '📨'} {sendingRequiredInfo ? 'Sending...' : 'Required Info'}
            </button>
<button className="quick-btn" style={{ borderColor: 'rgba(30,61,48,0.3)', color: 'var(--forest)', background: 'rgba(30,61,48,0.06)', fontWeight: 700 }} onClick={()=>{
              const w=window.open('','_blank','width=900,height=800')
              if(!w) return
              const today=new Date().toLocaleDateString('en-AU',{day:'numeric',month:'long',year:'numeric'})
              w.document.write(`<!DOCTYPE html><html><head><title>Stamp Duty Exemption — ${clientData.company_name}</title><style>*{box-sizing:border-box}body{margin:0;font-family:Arial,sans-serif;color:#1a2e25;background:#fff}.page{width:210mm;min-height:297mm;margin:0 auto}.head{background:#1e3d30;padding:20px 32px;display:flex;justify-content:space-between;align-items:center}.head img{height:40px;filter:brightness(0) invert(1)}.head-date{color:rgba(255,255,255,0.7);font-size:12px}.body{padding:32px 36px}.title{font-size:20px;font-weight:800;color:#1e3d30;margin-bottom:4px}.subtitle{font-size:12px;color:#6b8278;margin-bottom:24px}p{font-size:12.5px;line-height:1.85;margin:0 0 10px;color:#1a2e25}.guidance{background:#f0f4f2;border-left:4px solid #1e3d30;border-radius:6px;padding:14px 18px;margin-bottom:22px;font-size:12px;line-height:1.85}.guidance strong{color:#1e3d30}.declaration-box{border:1.5px solid #c8d8d0;border-radius:10px;padding:22px 24px;margin-bottom:22px}.declaration-title{font-size:11px;font-weight:700;color:#1e3d30;text-transform:uppercase;letter-spacing:.6px;margin-bottom:14px}.field-row{display:grid;grid-template-columns:160px 1fr;gap:8px;align-items:flex-end;margin-bottom:14px}.field-label{font-size:12px;font-weight:600;color:#6b8278}.field-line{border-bottom:1.5px solid #1e3d30;min-height:26px;font-size:13px;padding-bottom:2px}.sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:28px;margin-top:8px}.sig-block{border-top:2px solid #1e3d30;padding-top:8px}.sig-label{font-size:11px;color:#6b8278;margin-bottom:20px}.foot{border-top:1px solid #d0ddd7;padding:12px 32px;background:#f0f4f2;font-size:10px;color:#6b8278;line-height:1.7}.bar{position:fixed;top:16px;right:16px;display:flex;gap:8px;z-index:999}.btn{background:#1e3d30;color:#fff;border:none;padding:10px 20px;border-radius:10px;font-size:13px;font-weight:700;cursor:pointer}.cls{background:#6b8278}@media print{.bar{display:none}@page{size:A4;margin:0}}</style></head><body>
              <div class="bar"><button class="btn" onclick="window.print()">🖨️ Print / Save PDF</button><button class="btn cls" onclick="window.close()">Close</button></div>
              <div class="page">
                <div class="head"><img src="https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets/BIB-Dark-(Horizontal).png" alt="Benchmark"/><div class="head-date">${today}</div></div>
                <div class="body">
                  <div class="title">DECLARATION TO CLAIM THE NSW DUTIES ACT 1997</div>
                  <div class="subtitle">Small Business Stamp Duty Exemption — This declaration covers policies effected or renewed during the year ended 30 June 2026.</div>

                  <div class="guidance">
                    <strong>Guidance — please read before signing</strong><br/>
                    1. I hereby declare that I am a Capital Gains Tax small business entity (within the meaning of section 152-10 (1AA) of the Income Tax Assessment Act 1997 of the Commonwealth). I am a small business individual/partnership/company and/or trust, which is carrying on business, and the business has an aggregated turnover of less than <strong>$2 million</strong>. Aggregated turnover is your Australia wide annual turnover plus the annual turnovers of any business entities that are your affiliates or are connected with you.<br/>
                    2. A fraudulent declaration may invalidate your insurance contract.<br/>
                    3. The declaration covers all policies issued to you during the financial year ended 30 June 2026.<br/>
                    4. An insurer will place reliance on your declaration in charging the applicable insurance duty.<br/>
                    5. False declarations may result in penalties up to <strong>$11,000</strong> by Revenue NSW plus the insurance duty not paid and penal interest on that balance.<br/>
                    6. Revenue NSW may also be able to clarify your queries relating to the law and your obligations.
                  </div>

                  <div class="declaration-box">
                    <div class="declaration-title">Declaration</div>
                    <p>I declare that at the time the above policies are effected or renewed, I am/will be a "small business" as defined in section 259A of the Duties Act 1997 (NSW).</p>
                    <p>I have read and understand the Guidance provided above with this Declaration and do not have any questions.</p>
                    <p>I declare the above as the Insured or if not the Insured, do so with the authorisation of the Insured to declare on their behalf.</p>

                    <div style="margin-top:20px">
                      <div class="field-row"><div class="field-label">Insured Name</div><div class="field-line"></div></div>
                      <div class="field-row"><div class="field-label">ABN</div><div class="field-line"></div></div>
                      <div class="field-row"><div class="field-label">Print Name</div><div class="field-line"></div></div>
                      <div class="field-row"><div class="field-label">Contact Number</div><div class="field-line">${clientData.phone||''}</div></div>
                      <div class="field-row"><div class="field-label">Email</div><div class="field-line">${clientData.email||''}</div></div>
                    </div>

                    <div class="sig-grid" style="margin-top:24px">
                      <div class="sig-block"><div class="sig-label">Signature</div></div>
                      <div class="sig-block"><div class="sig-label">Date</div></div>
                    </div>
                  </div>

                  <p style="font-size:10.5px;color:#6b8278;margin-top:4px">This declaration should be retained by Benchmark Insurance Brokers for a minimum of 5 years. This document does not constitute legal or tax advice. The client is solely responsible for the accuracy of this declaration.</p>
                </div>
                <div class="foot"><strong>Benchmark Insurance Group Pty Ltd</strong> trading as <strong>Benchmark Insurance Brokers</strong> | ABN 33 694 361 549 | AR No. 001320555<br/>Authorised Representative of <strong>McLardy McShane Partners Pty Ltd</strong> | ABN 14 064 465 309 | AFSL 232987</div>
              </div></body></html>`)
              w.document.close(); w.focus()
            }}>📋 Stamp Duty</button>
            <button className="quick-btn" style={{ borderColor: 'rgba(30,61,48,0.3)', color: 'var(--forest)', background: 'rgba(30,61,48,0.06)', fontWeight: 700 }} onClick={() => setShowCoverConfirmed(true)}>✅ Cover Confirmed</button>
            <button className="quick-btn" style={{ borderColor: 'rgba(242,101,34,0.4)', color: 'var(--orange)', background: 'rgba(242,101,34,0.06)', fontWeight: 700 }} onClick={() => setShowTowerLifeReferral(true)}>🏢 Refer to Tower Life</button>
          </div>
        </div>
      )}

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 24px 24px' }}>
        {/* ── HEADER ── */}
        <div style={{
position: 'sticky', top: activeFfType || editingInfo ? 56 : 98, zIndex: 10,
          background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14,
          padding: '16px 20px', marginBottom: 16, marginTop: 16,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          maxWidth: '100%',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'linear-gradient(135deg, var(--forest), var(--forest-light))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 17, fontWeight: 700, color: '#fff', flexShrink: 0,
              boxShadow: '0 2px 8px rgba(30,61,48,0.3)',
            }}>{clientData.company_name?.substring(0, 2).toUpperCase()}</div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.3px' }}>{clientData.company_name}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {clientData.assigned_broker && <span style={{ fontSize: 11 }}>{clientData.assigned_broker}</span>}
                <CopyChip value={clientData.phone} label="No phone" icon="📞" />
                <CopyChip value={clientData.email} label="No email" icon="📧" />
                {(clientData.dob || factFinds[0]?.data?.ff_dob) && (
                  <CopyChip value={(() => { const d = clientData.dob || factFinds[0]?.data?.ff_dob; if (!d) return null; const [y,m,day] = d.split('-'); return `${parseInt(day)} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)-1]} ${y}` })()} label="No DOB" icon="🎂" />
                )}
                {daysSinceContact !== null && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                    background: daysSinceContact > 7 ? 'rgba(220,53,69,0.1)' : daysSinceContact > 3 ? 'rgba(230,168,23,0.12)' : 'rgba(30,61,48,0.08)',
                    color: daysSinceContact > 7 ? 'var(--danger)' : daysSinceContact > 3 ? '#7a5500' : 'var(--forest)',
                  }}>
                    {daysSinceContact === 0 ? '✓ Contacted today' : `Last contact ${daysSinceContact}d ago`}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
<select
  style={{ background: `${STATUS_COLORS[clientData.lead_status] || '#6b8278'}15`, border: `1.5px solid ${STATUS_COLORS[clientData.lead_status] || '#6b8278'}50`, borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600, color: STATUS_COLORS[clientData.lead_status] || '#6b8278', cursor: 'pointer', outline: 'none', fontFamily: 'var(--font)' }}
  value={clientData.lead_status}
  onChange={e => handleStatusChange(e.target.value)}
>
  {LEAD_STATUSES.map(s => <option key={s}>{s}</option>)}
</select>
            <button className="btn btn-secondary btn-sm" onClick={() => { setEditingInfo(true); setInfoForm({ company_name: clientData.company_name, phone: clientData.phone, email: clientData.email, address: clientData.address, contact_name: clientData.contact_name, referring_partner: clientData.referring_partner, sub_referrer: clientData.sub_referrer, assigned_broker: clientData.assigned_broker, notes: clientData.notes, dob: clientData.dob || '' }) }}>Edit</button>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {statusReasonModal && <StatusReasonModal status={statusReasonModal} onConfirm={reason => doStatusChange(statusReasonModal, reason)} onSkip={() => doStatusChange(statusReasonModal, null)} onCancel={() => setStatusReasonModal(null)} />}

        {/* ── EDIT INFO ── */}
        {editingInfo ? (
          <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 18, color: 'var(--text)' }}>Edit details</div>
            <div className="form-grid">
              <div className="full"><label className="form-label">Name</label><input className="form-input" value={infoForm.company_name || ''} onChange={e => setInfoForm(c => ({ ...c, company_name: e.target.value }))} /></div>
              <div><label className="form-label">Phone</label><input className="form-input" value={infoForm.phone || ''} onChange={e => setInfoForm(c => ({ ...c, phone: e.target.value }))} /></div>
              <div><label className="form-label">Email</label><input className="form-input" value={infoForm.email || ''} onChange={e => setInfoForm(c => ({ ...c, email: e.target.value }))} /></div>
              <div><label className="form-label">Contact Name</label><input className="form-input" value={infoForm.contact_name || ''} onChange={e => setInfoForm(c => ({ ...c, contact_name: e.target.value }))} /></div>
              <div><label className="form-label">Referring Partner</label><input className="form-input" value={infoForm.referring_partner || ''} onChange={e => setInfoForm(c => ({ ...c, referring_partner: e.target.value }))} /></div>
              {infoForm.referring_partner === 'Tower Life' && <div><label className="form-label">Referred to Tower by</label><input className="form-input" value={infoForm.sub_referrer || ''} onChange={e => setInfoForm(c => ({ ...c, sub_referrer: e.target.value }))} placeholder="e.g. Ray White Buderim" /></div>}
              <div><label className="form-label">Assigned Broker</label><select className="form-select" value={infoForm.assigned_broker || ''} onChange={e => setInfoForm(c => ({ ...c, assigned_broker: e.target.value }))}><option value="">— Not set —</option>{BROKERS.map(b => <option key={b}>{b}</option>)}</select></div>
              <div className="full"><label className="form-label">Address</label><input className="form-input" value={infoForm.address || ''} onChange={e => setInfoForm(c => ({ ...c, address: e.target.value }))} /></div>
              <div><label className="form-label">Date of Birth</label><input className="form-input" type="date" value={infoForm.dob || ''} onChange={e => setInfoForm(c => ({ ...c, dob: e.target.value }))} /></div>
              <div className="full"><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={infoForm.notes || ''} onChange={e => setInfoForm(c => ({ ...c, notes: e.target.value }))} /></div>
            </div>
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setEditingInfo(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveInfo} disabled={saving}>{saving ? 'Saving...' : 'Save details'}</button>
            </div>
          </div>

        /* ── ACTIVE FACT FIND ── */
        ) : activeFfType ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{FF_ICONS[activeFfType] || '📋'} {ffData._ff_label || activeFfType} — Fact Find</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{clientData.company_name}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="btn btn-secondary btn-sm" style={{ borderColor: 'rgba(230,168,23,0.6)', color: '#7a5500', background: 'rgba(230,168,23,0.1)', fontWeight: 700 }} onClick={() => { setCallScriptInitialTab('opening'); setShowCallScript(true) }}>📋 Opening Script</button>
                <button className="btn btn-secondary btn-sm" style={{ borderColor: 'rgba(230,168,23,0.6)', color: '#7a5500', background: 'rgba(230,168,23,0.1)', fontWeight: 700 }} onClick={() => { setCallScriptInitialTab('closing'); setShowCallScript(true) }}>🔚 Closing Script</button>
                <button className="btn btn-secondary btn-sm" onClick={() => printFactFindAsRoa({ ffData, ffType: activeFfType, clientData, brokerName: user.name })}>🖨️ Print ROA</button>
                <button className="btn btn-secondary btn-sm" onClick={cancelFactFind}>Cancel</button>
                <button className="btn btn-orange btn-sm" onClick={saveFactFind} disabled={saving}>{saving ? 'Saving...' : 'Save fact find'}</button>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: currentFfPct === 100 ? 'var(--forest)' : 'var(--muted)' }}>
                  {currentFfPct === 100 ? '✅ All fields complete' : `${currentFfPct}% complete`}
                </span>
                {showIncompleteWarning && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#c05c00', background: 'rgba(230,168,23,0.12)', padding: '3px 10px', borderRadius: 8, border: '1px solid rgba(230,168,23,0.3)' }}>
                    ⚠️ {ffIncompleteKeys.size} field{ffIncompleteKeys.size > 1 ? 's' : ''} still needed
                  </span>
                )}
              </div>
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${currentFfPct}%`, height: '100%', background: currentFfPct === 100 ? 'var(--forest)' : currentFfPct > 60 ? 'var(--forest)' : '#e6a817', borderRadius: 3, transition: 'width .3s ease' }} />
              </div>
            </div>

            {showIncompleteWarning && (
<div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(230,168,23,0.1)', border: '1.5px solid rgba(230,168,23,0.35)', borderRadius: 12, fontSize: 13, color: '#7a5500', fontWeight: 500 }}>
                Saved with incomplete fields — highlighted in amber below. Complete them before sending to client.
              </div>
            )}

            <FactFindSection
              policyType={activeFfType}
              ffData={ffData}
              onChange={setFfData}
              brokerName={user.name}
              highlightKeys={ffIncompleteKeys}
              allFactFinds={factFinds.length > 1 ? factFinds.reduce((acc, ff) => { acc[ff._ff_label || ff.type] = ff.data || {}; return acc }, {}) : null}
              compareWith={compareWith}
              setCompareWith={setCompareWith}
              compareData={compareWith ? (factFinds.find(ff => (ff._ff_label || ff.type) === compareWith)?.data || {}) : null}
              compareOnChange={compareWith ? ((updater) => {
                const targetFf = factFinds.find(ff => (ff._ff_label || ff.type) === compareWith)
                if (!targetFf) return
                const newData = typeof updater === 'function' ? updater(targetFf.data || {}) : updater
                const updatedFactFinds = factFinds.map(ff => (ff.id || ff._ff_id) === (targetFf.id || targetFf._ff_id) ? { ...ff, data: newData, updated_at: new Date().toISOString() } : ff)
                setClientData(prev => ({ ...prev, fact_finds: updatedFactFinds }))
                supabase.from('clients').update({ fact_finds: updatedFactFinds }).eq('id', clientData.id)
              }) : null}
            />

            <div className="form-actions" style={{ marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={cancelFactFind}>Cancel</button>
              <button className="btn btn-orange" onClick={saveFactFind} disabled={saving}>{saving ? 'Saving...' : 'Save fact find'}</button>
            </div>
          </div>
        ) : (
          <div>
            {/* ── STRIP ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Status', value: clientData.lead_status || 'Client', color: STATUS_COLORS[clientData.lead_status] || 'var(--forest)' },
                { label: 'Broker', value: clientData.assigned_broker?.split(' ')[0] || '—' },
                { label: 'Cover Type', value: clientData.policy_type || factFinds[0]?.type || '—' },
              ].map(item => (
                <div key={item.label} style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>{item.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: item.color || 'var(--text)' }}>{item.value}</div>
                </div>
              ))}
              <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>Renewal</div>
                <InlineRenewalEdit value={clientData.renewal_date} onSave={saveRenewalDate} fmtDate={fmtDate} />
              </div>
            </div>

            {/* ── TABS ── */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1.5px solid var(--border)', flexWrap: 'wrap' }}>
              {tabs.map(({ key, label, highlight }) => {
                const isActive = activeTab === key
                return (
                  <button key={key} onClick={() => handleTabChange(key)} style={{
                    padding: highlight ? '6px 14px' : '8px 14px',
                    background: highlight ? (isActive ? 'var(--orange)' : 'rgba(242,101,34,0.08)') : 'none',
                    border: highlight ? `1.5px solid ${isActive ? 'var(--orange)' : 'rgba(242,101,34,0.25)'}` : 'none',
                    borderBottom: highlight ? (isActive ? '2px solid var(--orange)' : '1.5px solid rgba(242,101,34,0.25)') : (isActive ? '2px solid var(--forest)' : '2px solid transparent'),
                    borderRadius: highlight ? '8px 8px 0 0' : 0,
                    fontWeight: isActive ? 700 : 500,
                    color: highlight ? (isActive ? '#fff' : 'var(--orange)') : (isActive ? 'var(--forest)' : 'var(--muted)'),
                    fontSize: 12, cursor: 'pointer', marginBottom: -1.5, fontFamily: 'var(--font)', whiteSpace: 'nowrap', transition: 'all .15s',
                  }}>{label}</button>
                )
              })}
            </div>

{/* ── OVERVIEW ── */}
            {activeTab === 'overview' && (
<div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, alignItems: 'start' }}>

                {/* ── LEFT: Unified activity feed ── */}
                <div>
                  {/* Add note inline */}
                  <div style={{ marginBottom: 14 }}>
                    <textarea
                      className="form-input note-textarea"
                      placeholder="Add a note..."
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      rows={3}
                      style={{ marginBottom: 6, color: 'var(--text)', fontSize: 13 }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={addNote} disabled={addingNote || !newNote.trim()}>
                      {addingNote ? 'Adding...' : 'Add note'}
                    </button>
                  </div>

                  {/* Filter chips */}
                  {(() => {
                    const [feedFilter, setFeedFilter] = [
                      window.__feedFilter || 'all',
                      (v) => { window.__feedFilter = v; setNewNote(n => n) }
                    ]
                    return null
                  })()}

                  <OverviewFeed
                    auditEvents={auditEvents}
                    clientTexts={clientTexts}
                    clientCalls={clientCalls}
                    fmtDate={fmtDate}
                    openClient={null}
                    handleTabChange={handleTabChange}
                  />
                </div>

                {/* ── RIGHT: Client info panel ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                  {/* Compliance */}
                  <ComplianceProgress complianceState={complianceState} />

                  {/* Contact details */}
                  <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--forest)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                      Contact
                    </div>
                    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {[
                        ['Phone', clientData.phone, '📞'],
                        ['Email', clientData.email, '📧'],
                        ['Contact', clientData.contact_name, '👤'],
                        ['Address', clientData.address, '📍'],
                      ].map(([label, value, icon]) => value ? (
                        <div key={label}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 2 }}>{label}</div>
                          <CopyChip value={value} icon={icon} />
                        </div>
                      ) : null)}
                    </div>
                  </div>

                  {/* Business info */}
                  <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--forest)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                      Details
                    </div>
                    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        ['Broker', clientData.assigned_broker],
                        ['Lead Source', clientData.lead_source],
                        ['Partner', clientData.referring_partner],
                        ['Classification', complianceState.effective_classification || (RETAIL_TYPES.some(t => (clientData.policy_type || '').toLowerCase().includes(t.toLowerCase())) ? 'Retail' : 'Wholesale')],
                        ['Cover Type', clientData.policy_type || factFinds[0]?.type],
                      ].map(([label, value]) => value ? (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>{label}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{value}</span>
                        </div>
                      ) : null)}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 4, borderTop: '1px solid var(--border)' }}>
                        <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>Renewal</span>
                        <InlineRenewalEdit value={clientData.renewal_date} onSave={saveRenewalDate} fmtDate={fmtDate} />
                      </div>
                    </div>
                  </div>

                  {/* Fact find rings */}
                  {factFinds.length > 0 && (
                    <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--forest)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Fact Finds</div>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => handleTabChange('factfinds')}>View all</button>
                      </div>
                      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {factFinds.map(ff => {
                          const pct = getCompletionPct(ff)
                          const label = ff._ff_label || ff.type
                          return (
                            <div key={ff.id || ff._ff_id || ff.type} onClick={() => handleTabChange('factfinds')} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '6px 8px', borderRadius: 8, transition: 'background .12s' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                              <ProgressRing pct={pct} size={36} stroke={3} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{FF_ICONS[ff.type] || '📋'} {label}</div>
                                <div style={{ fontSize: 10, color: pct === 100 ? 'var(--forest)' : 'var(--muted)', fontWeight: 600 }}>{pct === 100 ? '✓ Complete' : `${pct}% complete`}</div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

{/* Tower Life life insurance referral-back */}
                  {clientData.referring_partner === 'Tower Life' && clientData.life_referral === 'yes' && clientData.lead_status === 'Policy Placed' && !clientData.life_referral_sent && (
                    <div style={{ padding: '14px 16px', background: 'rgba(242,101,34,0.06)', border: '1.5px solid rgba(242,101,34,0.3)', borderRadius: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange)', marginBottom: 6 }}>🔄 Refer back to Tower Life</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>Isaac flagged this client needs life insurance advice. Send referral when ready.</div>
                      <button className="btn btn-orange btn-sm" style={{ width: '100%', fontSize: 12 }}
                        onClick={async () => {
                          if (!window.confirm(`Refer ${clientData.company_name} back to Tower Life for life insurance?`)) return
                          try {
                            const { data: { session } } = await supabase.auth.getSession()
                            const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/submit-referral`, {
                              method: 'POST',
                              headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ _action: 'refer_back_to_tower_life', _client_id: clientData.id, _broker_name: user.name }),
                            })
                            const result = await res.json()
                            if (!result.success) throw new Error(result.error)
                            setClientData(prev => ({ ...prev, life_referral_sent: true }))
                            await writeClientHistory({
                              auditEntry: makeAuditEntry('email', 'Life insurance referral sent to Tower Life', user.name, [clientData.company_name]),
                              activityEntry: makeActivityEntry('email', `Life insurance referral sent to Tower Life for ${clientData.company_name} by ${user.name}`, '🔄'),
                            })
                            showToast('✅ Referral sent to Tower Life')
                          } catch(e) { showToast('Failed to send referral — try again') }
                        }}
                      >
                        🔄 Refer to Tower Life for Life Insurance
                      </button>
                    </div>
                  )}

                  {clientData.referring_partner === 'Tower Life' && clientData.life_referral_sent && (
                    <div style={{ padding: '10px 14px', background: 'rgba(30,61,48,0.06)', border: '1.5px solid var(--forest-border)', borderRadius: 10, fontSize: 11, color: 'var(--forest)', fontWeight: 600 }}>
                      ✓ Life insurance referral sent to Tower Life
                    </div>
                  )}

                  {/* Referral partner update note */}
                  {clientData.referring_partner && (
                    <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        Update for {clientData.referring_partner}
                      </div>
                      <div style={{ padding: '12px 16px' }}>
                        <textarea
                          className="form-input"
                          rows={3}
                          style={{ fontSize: 12 }}
                          placeholder="e.g. Called, wants a life insurance callback next week"
                          defaultValue={clientData.referral_note || ''}
                          onBlur={async e => {
                            const val = e.target.value
                            if (val === (clientData.referral_note || '')) return
                            await supabase.from('clients').update({ referral_note: val }).eq('id', clientData.id)
                            setClientData(prev => ({ ...prev, referral_note: val }))
                            showToast('Referral note saved')
                          }}
                        />
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Included in the weekly report sent to your team</div>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {clientData.notes && (
                    <div style={{ padding: '14px 16px', background: 'rgba(230,168,23,0.06)', border: '1px solid rgba(230,168,23,0.2)', borderRadius: 12, fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#7a5500', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Notes</div>
                      {clientData.notes}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── FACT FINDS (multi-instance) ── */}
            {activeTab === 'factfinds' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Fact Finds ({factFinds.length})</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>Add:</span>
                    <select className="form-select" style={{ width: 'auto', fontSize: 13, padding: '6px 12px' }} defaultValue="" onChange={e => { if (e.target.value) startFactFind(e.target.value); e.target.value = '' }}>
                      <option value="" disabled>Select cover type...</option>
                      {FACT_FIND_OPTIONS.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                {factFinds.length === 0
                  ? <div className="empty" style={{ padding: '40px 24px' }}><div className="empty-icon">📋</div><div className="empty-title">No fact finds yet</div><div className="empty-sub">Select a cover type above to start your first fact find</div></div>
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {factFinds.map(ff => {
                        const pct = getCompletionPct(ff)
                        const label = ff._ff_label || ff.type
                        const ffId = ff.id || ff._ff_id || ff.type
                        return (
                          <div key={ffId} style={{
                            background: 'var(--surface)', border: '1.5px solid var(--border)',
                            borderRadius: 14, padding: '16px 20px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.04)', transition: 'border-color .15s, box-shadow .15s',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                              <ProgressRing pct={pct} size={48} stroke={4} />
                              <div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{FF_ICONS[ff.type] || '📋'} {label}</div>
                                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3, display: 'flex', gap: 8 }}>
                                  <span>{ff.type}</span>
                                  <span>·</span>
                                  <span>{ff.updated_at ? new Date(ff.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span>
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
<button className="btn btn-secondary btn-sm" onClick={() => setViewingFf(ff)}>View</button>
<button className="btn btn-secondary btn-sm" onClick={() => editFactFind(ff)}>Edit</button>
<button className="btn btn-secondary btn-sm" style={{ color:'var(--forest)', borderColor:'var(--forest-border)', background:'var(--forest-subtle)', fontWeight:700 }} onClick={() => analyseFactFind(ff)}>✨ Analyse</button>
<button className="btn btn-secondary btn-sm" onClick={() => printFactFindAsRoa({ ffData: ff.data || {}, ffType: ff.type, clientData, brokerName: user.name })}>🖨️</button>
                          
                            </div>
                          </div>
                        )
                      })}
                    </div>
                }
              </div>
            )}

            {/* ── ACTIVITY ── */}
            {activeTab === 'audit' && (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <textarea className="form-input note-textarea" placeholder="Add a note..." value={newNote} onChange={e => setNewNote(e.target.value)} rows={2} style={{ marginBottom: 8, color: 'var(--text)' }} />
                  <button className="btn btn-primary btn-sm" onClick={addNote} disabled={addingNote || !newNote.trim()}>{addingNote ? 'Adding...' : 'Add note'}</button>
                </div>
                <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <input className="form-input" placeholder="🔍 Search activity..." value={auditSearch} onChange={e => setAuditSearch(e.target.value)} style={{ fontSize: 13 }} />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {AUDIT_FILTER_TYPES.map(({ key, label }) => (
                      <button key={key} onClick={() => setAuditFilter(key)} style={{ padding: '4px 12px', borderRadius: 20, border: `1.5px solid ${auditFilter === key ? 'var(--forest)' : 'var(--border)'}`, background: auditFilter === key ? 'var(--forest)' : 'var(--surface)', color: auditFilter === key ? '#fff' : 'var(--muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all .15s', boxShadow: auditFilter === key ? '0 2px 6px rgba(30,61,48,0.2)' : 'none' }}>
                        {label}{key !== 'all' && auditEvents.filter(e => e.type === key).length > 0 ? ` (${auditEvents.filter(e => e.type === key).length})` : ''}
                      </button>
                    ))}
                  </div>
                </div>
                {filteredAuditEvents.length === 0
                  ? <div className="empty" style={{ padding: '32px 24px' }}><div className="empty-icon">📋</div><div className="empty-title">No entries found</div></div>
                  : <div className="timeline">
                      {filteredAuditEvents.map(entry => (
                        <div key={entry.key} className="timeline-item">
                          <div className="timeline-dot" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>{AUDIT_ICONS[entry.type] || '🔹'}</div>
                          <div className="timeline-content">
                            <div className="timeline-text" style={{ color: 'var(--text)' }}>{entry.title}</div>
                            {entry.changes?.length > 0 && (
                              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {entry.changes.map((change, i) => <div key={i} style={{ fontSize: 11, color: 'var(--text)', background: 'var(--surface)', padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)' }}>{change}</div>)}
                              </div>
                            )}
                            <div className="timeline-time" style={{ marginTop: 4 }}>{entry.actor} · {new Date(entry.timestamp).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                }
              </div>
            )}

            {/* ── DOCUMENTS ── */}
            {activeTab === 'documents' && <DocsTabNew clientId={clientData.id} clientName={clientData.company_name} documentsWithNames={documentsWithNames} coverConfirmations={savedCoverConfirmeds} onFilesSelected={handleDocumentUpload} loadAll={loadAll} showToast={showToast} userName={user.name} />}

            {/* ── CALLS ── */}
            {activeTab === 'calls' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <input className="form-input" style={{ flex: 1, fontSize: 13, color: 'var(--text)' }} placeholder="🔍 Search transcripts..." value={callSearch} onChange={e => setCallSearch(e.target.value)} />
                  <button className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={handleSyncCalls} disabled={syncingCalls}>{syncingCalls ? 'Syncing...' : '📞 Sync Calls'}</button>
                </div>
                {filteredCalls.length === 0
                  ? <div className="empty" style={{ padding: '32px 24px' }}><div className="empty-icon">📞</div><div className="empty-title">{callSearch ? 'No transcripts match' : 'No calls found'}</div><div className="empty-sub">{callSearch ? 'Try different keywords' : 'Hit Sync Calls to pull in your Aircall history'}</div></div>
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {filteredCalls.map(call => {
                        const isMissed = !call.duration || call.duration < 5
                        const isInbound = call.direction === 'inbound'
                        return (
                          <div key={call.id} style={{ background: 'var(--surface)', border: `1.5px solid ${isMissed ? 'rgba(220,53,69,0.2)' : 'var(--border)'}`, borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: call.transcript ? 10 : 0 }}>
                              <div style={{ width: 36, height: 36, borderRadius: 8, background: isMissed ? 'rgba(220,53,69,0.1)' : isInbound ? 'rgba(59,130,246,0.1)' : 'rgba(30,61,48,0.1)', color: isMissed ? 'var(--danger)' : isInbound ? '#3b82f6' : 'var(--forest)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 9, flexShrink: 0, gap: 1 }}>
                                <span style={{ fontSize: 14 }}>{isMissed ? '📵' : isInbound ? '📲' : '📞'}</span>
                                <span>{isMissed ? 'MISS' : isInbound ? 'IN' : 'OUT'}</span>
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: isMissed ? 'var(--danger)' : 'var(--text)' }}>
                                  {isMissed ? 'Missed call' : `${call.direction} call`}
                                  {!isMissed && <span style={{ fontWeight: 400, color: 'var(--muted)' }}> · {fmtDuration(call.duration)}</span>}
                                  {call.broker_name && call.broker_name !== 'Unknown' && <span style={{ fontWeight: 400, color: 'var(--muted)' }}> · {call.broker_name}</span>}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{call.started_at ? new Date(call.started_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</div>
                              </div>
                            </div>
                            {call.transcript && <TranscriptBlock transcript={call.transcript} search={callSearch} />}
                          </div>
                        )
                      })}
                    </div>
                }
              </div>
            )}

            {/* ── QUOTES ── */}
            {activeTab === 'quotes' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {['all', 'sent', 'draft'].map(f => (
                      <button key={f} onClick={() => setQuoteStatusFilter(f)} style={{ padding: '5px 14px', borderRadius: 20, border: `1.5px solid ${quoteStatusFilter === f ? 'var(--forest)' : 'var(--border)'}`, background: quoteStatusFilter === f ? 'var(--forest)' : 'var(--surface)', color: quoteStatusFilter === f ? '#fff' : 'var(--muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', textTransform: 'capitalize', boxShadow: quoteStatusFilter === f ? '0 2px 6px rgba(30,61,48,0.2)' : 'none' }}>{f}</button>
                    ))}
                  </div>
                  <button className="btn btn-orange btn-sm" onClick={() => setShowQuotePack(true)}>+ New Quote Pack</button>
                </div>
                {loadingArtifacts
                  ? <div className="loading"><div className="spinner" /><span>Loading...</span></div>
                  : filteredQuotePacks.length === 0
                    ? <div className="empty" style={{ padding: '32px 24px' }}><div className="empty-icon">📦</div><div className="empty-title">No quote packs {quoteStatusFilter !== 'all' ? `with status "${quoteStatusFilter}"` : 'yet'}</div></div>
                    : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {filteredQuotePacks.map(pack => (
                          <div key={pack.id} style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{ fontSize: 22 }}>📦</div>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{pack.title || 'Quote Pack'}</div>
                                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <span>{pack.created_by || '—'}</span><span>·</span>
                                  <span>{pack.created_at ? new Date(pack.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span><span>·</span>
                                  <span style={{ fontWeight: 700, color: pack.status === 'Sent' ? 'var(--forest)' : 'var(--warning)' }}>{pack.status || 'Draft'}</span>
                                </div>
                              </div>
                            </div>
                            <button className="btn btn-secondary btn-sm" onClick={() => openHtmlInWindow(pack.html_preview || '', pack.title || 'Quote Pack')}>Open</button>
                          </div>
                        ))}
                      </div>
                }
              </div>
            )}

            {activeTab === 'coverconfirmed' && <CoversTab clientId={clientData.id} onNewCC={() => setShowCoverConfirmed(true)} />}

            {/* ── TEXTS ── */}
            {activeTab === 'texts' && (
              <div style={{ position: 'relative' }}>
                {clientTexts.length === 0
                  ? <div className="empty" style={{ padding: '32px 24px' }}><div className="empty-icon">💬</div><div className="empty-title">No texts yet</div></div>
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 60 }}>
                      {clientTexts.map(text => (
                        <div key={text.id} style={{
                          alignSelf: text.direction === 'outbound' ? 'flex-end' : 'flex-start',
                          maxWidth: '75%',
                          background: text.direction === 'outbound' ? 'var(--forest)' : 'var(--surface)',
                          color: text.direction === 'outbound' ? '#fff' : 'var(--text)',
                          border: text.direction === 'outbound' ? 'none' : '1.5px solid var(--border)',
                          borderRadius: text.direction === 'outbound' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                          padding: '10px 14px',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        }}>
                          <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{text.body}</div>
                          <div style={{ fontSize: 11, marginTop: 6, color: text.direction === 'outbound' ? 'rgba(255,255,255,0.7)' : 'var(--muted)' }}>{text.direction}{text.broker_name ? ` · ${text.broker_name}` : ''} · {new Date(text.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      ))}
                      <div ref={textsEndRef} />
                    </div>
                }
                {/* Floating compose button */}
                <div style={{ position: 'sticky', bottom: 16, display: 'flex', justifyContent: 'center' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => { setSmsInitialTpl(null); setShowSms(true) }}
                    disabled={!clientData.phone}
                    style={{ borderRadius: 50, padding: '12px 28px', fontSize: 14, fontWeight: 700, boxShadow: '0 4px 16px rgba(30,61,48,0.3)' }}
                  >💬 Compose SMS</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── MODALS ── */}
      {policyModal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setPolicyModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            <div className="modal-head">
              <div><div className="modal-title">Policy Placed</div><div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{clientData.company_name}</div></div>
              <button className="modal-close" onClick={() => setPolicyModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="full"><label className="form-label">Policy type</label><select className="form-select" value={policyForm.policy_type || ''} onChange={e => setPolicyForm(c => ({ ...c, policy_type: e.target.value }))}><option value="">Select cover type...</option>{FACT_FIND_OPTIONS.map(t => <option key={t}>{t}</option>)}</select></div>
                <div><label className="form-label">Policy start date</label><input className="form-input" type="date" value={policyForm.start_date || ''} onChange={e => setPolicyForm(c => ({ ...c, start_date: e.target.value }))} /></div>
                <div><label className="form-label">Renewal date *</label><input className="form-input" type="date" value={policyForm.renewal_date || ''} onChange={e => setPolicyForm(c => ({ ...c, renewal_date: e.target.value }))} /></div>
              </div>
              <div className="form-actions">
                <button className="btn btn-secondary" onClick={() => setPolicyModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={confirmPolicyPlaced} disabled={savingPolicy || !policyForm.renewal_date}>{savingPolicy ? 'Saving...' : 'Confirm placement →'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── REQUIRED INFO CONFIRM ── */}
      {showRequiredInfoConfirm && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowRequiredInfoConfirm(false)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <div className="modal-title">📨 Send Required Info?</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{clientData.company_name} · {clientData.email}</div>
              </div>
              <button className="modal-close" onClick={() => setShowRequiredInfoConfirm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Email variant</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['Retail', 'Wholesale', 'Claims-Made'].map(v => {
                    const autoDetected = complianceState.is_claims_made ? 'Claims-Made' : complianceState.effective_classification === 'Wholesale' ? 'Wholesale' : 'Retail'
                    const selected = (requiredInfoVariantOverride || autoDetected) === v
                    return (
                      <button key={v} onClick={() => setRequiredInfoVariantOverride(v === autoDetected ? null : v)}
                        style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `2px solid ${selected ? 'var(--forest)' : 'var(--border)'}`, background: selected ? 'var(--forest)' : 'var(--surface)', color: selected ? '#fff' : 'var(--muted)', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all .15s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <span>{v === 'Retail' ? '🏠 Retail' : v === 'Wholesale' ? '🏢 Wholesale' : '⚖️ Claims-Made'}</span>
                        {v === autoDetected && <span style={{ fontSize: 9, opacity: 0.7 }}>auto-detected</span>}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div style={{ background: 'rgba(30,61,48,0.05)', border: '1.5px solid var(--forest-border)', borderRadius: 12, padding: '14px 16px', marginBottom: 16, fontSize: 13, lineHeight: 1.8, color: 'var(--text)' }}>
                <strong>Sending to:</strong> {clientData.email}<br />
                {(() => {
                  const variant = requiredInfoVariantOverride || (complianceState.is_claims_made ? 'Claims-Made' : complianceState.effective_classification === 'Wholesale' ? 'Wholesale' : 'Retail')
                  return ['📄 Financial Services Guide', '📄 Terms of Engagement', '🔒 Privacy Policy', '📋 Benchmark Important Information', '📜 Code of Practice (NIBA)',
                    variant === 'Wholesale' ? '⚖️ Duty of Disclosure' : variant === 'Claims-Made' ? '⚖️ Duty of Disclosure + ⚠️ Claims-Made Notice' : '⚖️ Duty to Take Reasonable Care (misrepresentation) + TMD notice',
                    complianceState.advice_mode === 'Personal Advice' ? '📝 Personal Advice Record' : '📝 General Advice Warning',
                  ].map((item, i) => (<div key={i} style={{ fontSize: 12, display: 'flex', gap: 8 }}><span>•</span>{item}</div>))
                })()}
              </div>
{clientData.phone && (requiredInfoVariantOverride || (complianceState.is_claims_made ? 'Claims-Made' : complianceState.effective_classification === 'Wholesale' ? 'Wholesale' : 'Retail')) === 'Retail' && (
                <div onClick={() => setSendConsentSms(s => !s)} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', marginBottom: 16, borderRadius: 10, border: `1.5px solid ${sendConsentSms ? 'var(--forest)' : 'var(--border)'}`, background: sendConsentSms ? 'rgba(30,61,48,0.05)' : 'var(--surface2)', cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${sendConsentSms ? 'var(--forest)' : 'var(--border)'}`, background: sendConsentSms ? 'var(--forest)' : '#fff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0, marginTop: 1 }}>{sendConsentSms ? '✓' : ''}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: sendConsentSms ? 'var(--forest)' : 'var(--text)' }}>📱 Also send commission consent SMS</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, lineHeight: 1.5 }}>Texts {clientData.phone} asking the client to reply YES to confirm commission consent</div>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowRequiredInfoConfirm(false)}>Cancel</button>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={sendingRequiredInfo}
                  onClick={() => { setShowRequiredInfoConfirm(false); sendRequiredInfoEmail(requiredInfoVariantOverride) }}>
                  {sendingRequiredInfo ? 'Sending...' : 'Confirm & Send →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRequiredInfoOverride && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowRequiredInfoOverride(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">⚠️ Send Required Info Again?</div>
              <button className="modal-close" onClick={() => setShowRequiredInfoOverride(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background: 'rgba(230,168,23,0.08)', border: '1.5px solid rgba(230,168,23,0.3)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, fontSize: 13, lineHeight: 1.7, color: 'var(--text)' }}>
                <strong>Required info was already sent</strong> on {complianceState.important_info_sent_at ? new Date(complianceState.important_info_sent_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}.<br /><br />
                Sending again is fine for cross-sells or new policies.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowRequiredInfoOverride(false)}>Cancel</button>
                <button className="btn btn-orange" style={{ flex: 1 }} onClick={() => { setShowRequiredInfoOverride(false); setRequiredInfoVariantOverride(null); setShowRequiredInfoConfirm(true) }}>Review & Send Again</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── VIEW FACT FIND (read-only) ── */}
      {analysingFf && (
    <div className="overlay" onClick={e => e.target === e.currentTarget && setAnalysingFf(null)}>
      <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="modal-title">✨ AI Analysis — {analysingFf._ff_label || analysingFf.type}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{clientData.company_name} · {analysingFf.type}</div>
          </div>
          <button className="modal-close" onClick={() => { setAnalysingFf(null); setFfAnalysis(null) }}>✕</button>
        </div>
        <div className="modal-body">
          {analysisLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', gap: 16 }}>
              <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>Analysing fact find...</div>
            </div>
          ) : ffAnalysis ? (
            <div>
              <div style={{ background: 'rgba(30,61,48,0.04)', border: '1.5px solid var(--forest-border)', borderRadius: 12, padding: '16px 20px', fontSize: 13, lineHeight: 1.8, whiteSpace: 'pre-wrap', color: 'var(--text)' }}>
                {ffAnalysis}
              </div>
              <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard.writeText(ffAnalysis); showToast('Analysis copied') }}>Copy</button>
                <button className="btn btn-secondary btn-sm" onClick={() => analyseFactFind(analysingFf)}>↻ Re-run</button>
                <button className="btn btn-primary btn-sm" onClick={() => { setAnalysingFf(null); setFfAnalysis(null) }}>Done</button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )}

{viewingFf && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setViewingFf(null)}>
          <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <div className="modal-title">{viewingFf._ff_label || viewingFf.type} — Fact Find</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>Read only · {viewingFf.type} · {viewingFf.updated_at ? new Date(viewingFf.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => printFactFindAsRoa({ ffData: viewingFf.data || {}, ffType: viewingFf.type, clientData, brokerName: user.name })}>🖨️ Print ROA</button>
                <button className="btn btn-secondary btn-sm" onClick={() => { setViewingFf(null); editFactFind(viewingFf) }}>Edit</button>
                <button className="modal-close" onClick={() => setViewingFf(null)}>✕</button>
              </div>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
{(() => {
  const template = FACT_FINDS[viewingFf.type]
  if (!template) return <div style={{ color: 'var(--muted)' }}>No template found</div>
  const data = viewingFf.data || {}
  const groups = []
  let currentGroup = null

  // Handle both flat section-header arrays (Cyber) and sections[].fields[] structure (all others)
  const isFlatStructure = template.sections.some(s => s.type === 'section-header')
  const flatItems = isFlatStructure
    ? template.sections
    : template.sections.flatMap(s => [
        ...(s.title ? [{ type: 'section-header', label: s.title }] : []),
        ...s.fields
      ])

  flatItems.forEach(item => {
    if (item.type === 'section-header') {
      currentGroup = { title: item.label, fields: [] }
      groups.push(currentGroup)
      return
    }
    if (!item.key) return
    if (item.showIf && data[item.showIf.key] !== item.showIf.value) return
    const v = data[item.key]
    if (!v || v === '' || (Array.isArray(v) && v.length === 0)) return
    if (!currentGroup) { currentGroup = { title: 'General', fields: [] }; groups.push(currentGroup) }
    currentGroup.fields.push({ label: item.label, value: formatFfFieldValue(item, v, '\n') })
  })

  return groups.filter(g => g.fields.length > 0).map((group, gi) => (
    <div key={gi} style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--forest)', textTransform: 'uppercase', letterSpacing: '.6px', borderBottom: '2px solid var(--forest-border)', paddingBottom: 6, marginBottom: 12 }}>{group.title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {group.fields.map((f, fi) => (
          <div key={fi} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>{f.label}</div>
            <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, whiteSpace: 'pre-line' }}>{f.value}</div>
          </div>
        ))}
      </div>
    </div>
  ))
})()}
            </div>
          </div>
        </div>
      )}



      {showPostBindWarning && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowPostBindWarning(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <div className="modal-title">⚠️ This fact find is linked to a Cover Confirmed</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{showPostBindWarning._ff_label || showPostBindWarning.type}</div>
              </div>
              <button className="modal-close" onClick={() => setShowPostBindWarning(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background: 'rgba(230,168,23,0.08)', border: '1.5px solid rgba(230,168,23,0.3)', borderRadius: 10, padding: '14px 16px', marginBottom: 20, fontSize: 13, lineHeight: 1.8 }}>
                A Cover Confirmed has already been issued using this fact find. The compliance snapshot sent to the broker is <strong>frozen and unaffected</strong> by any edits here.<br /><br />
                Please confirm why you are editing this fact find:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                <button
                  onClick={async () => {
                    await writeClientHistory({
                      auditEntry: makeAuditEntry('factfind', `Post-bind fact find edit — all details remain the same: ${showPostBindWarning._ff_label || showPostBindWarning.type}`, user.name),
                      activityEntry: makeActivityEntry('factfind', `Fact find reviewed post-bind — no changes to client details (${showPostBindWarning._ff_label || showPostBindWarning.type}) by ${user.name}`, '📋'),
                    })
                    doEditFactFind(showPostBindWarning)
                  }}
                  style={{ padding: '14px 16px', borderRadius: 10, border: '1.5px solid var(--forest-border)', background: 'rgba(30,61,48,0.06)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font)', fontSize: 13 }}
                >
                  <div style={{ fontWeight: 700, color: 'var(--forest)', marginBottom: 4 }}>✅ All details remain the same</div>
                  <div style={{ color: 'var(--muted)', fontSize: 12 }}>I'm reviewing or correcting a minor error — no material change to client circumstances</div>
                </button>
                <button
                  onClick={async () => {
                    await writeClientHistory({
                      auditEntry: makeAuditEntry('factfind', `Post-bind fact find edit — client details updated: ${showPostBindWarning._ff_label || showPostBindWarning.type}`, user.name, ['Client confirmed updated details — new Cover Confirmed may be required']),
                      activityEntry: makeActivityEntry('factfind', `Fact find updated post-bind — client details changed (${showPostBindWarning._ff_label || showPostBindWarning.type}) by ${user.name}`, '📋'),
                    })
                    doEditFactFind(showPostBindWarning)
                  }}
                  style={{ padding: '14px 16px', borderRadius: 10, border: '1.5px solid rgba(242,101,34,0.4)', background: 'rgba(242,101,34,0.06)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font)', fontSize: 13 }}
                >
                  <div style={{ fontWeight: 700, color: 'var(--orange)', marginBottom: 4 }}>✏️ I have updated client details</div>
                  <div style={{ color: 'var(--muted)', fontSize: 12 }}>Client circumstances have changed — this will be logged and a new Cover Confirmed may be required</div>
                </button>
              </div>
              <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => setShowPostBindWarning(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showSms && <SmsComposeModal client={clientData} user={user} onSend={handleSendSms} onClose={() => { setShowSms(false); setSmsInitialTpl(null) }} sending={sendingSms} initialTemplate={smsInitialTpl} />}

      {showTowerLifeReferral && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowTowerLifeReferral(false)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <div className="modal-title">🏢 Refer to Tower Life Insurance</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{clientData.company_name}</div>
              </div>
              <button className="modal-close" onClick={() => { setShowTowerLifeReferral(false); setTowerLifeNote('') }}>✕</button>
            </div>
            <div className="modal-body">

              {/* Client details preview */}
              <div style={{ background: 'var(--surface2)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Client Details Being Sent</div>
                {[
                  ['Client', clientData.company_name],
                  ['Phone', clientData.phone],
                  ['Email', clientData.email],
                  ['Cover Type', clientData.policy_type || '—'],
                  ['Broker', user.name],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{label}</span>
                    <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500 }}>{value || '—'}</span>
                  </div>
                ))}
              </div>

              {/* Free text note */}
              <div style={{ marginBottom: 20 }}>
                <label className="form-label">Message to Isaac <span style={{ fontWeight: 400, color: 'var(--muted)' }}>— optional, appears in the email</span></label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={towerLifeNote}
                  onChange={e => setTowerLifeNote(e.target.value)}
                  placeholder="e.g. Client is open to a life insurance conversation after we discussed their general insurance needs. Good time to reach out."
                  autoFocus
                />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setShowTowerLifeReferral(false); setTowerLifeNote('') }}>Cancel</button>
                <button className="btn btn-orange" style={{ flex: 2 }} disabled={sendingTowerLife}
                  onClick={async () => {
                    setSendingTowerLife(true)
                    try {
                      const { data: { session } } = await supabase.auth.getSession()
                      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/submit-referral`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          _action: 'refer_back_to_tower_life',
                          _client_id: clientData.id,
                          _broker_name: user.name,
                          _note: towerLifeNote,
                        }),
                      })
                      const result = await res.json()
                      if (!result.success) throw new Error(result.error)
                      await writeClientHistory({
                        auditEntry: makeAuditEntry('email', 'Life insurance referral sent to Tower Life', user.name, [clientData.company_name, towerLifeNote].filter(Boolean)),
                        activityEntry: makeActivityEntry('email', `Life insurance referral sent to Tower Life for ${clientData.company_name} by ${user.name}`, '🏢'),
                      })
                      showToast('✅ Referral sent to Tower Life')
                      setShowTowerLifeReferral(false)
                      setTowerLifeNote('')
                    } catch(e) { showToast('Failed to send — try again') }
                    setSendingTowerLife(false)
                  }}
                >
                  {sendingTowerLife ? 'Sending...' : '🏢 Send Referral to Isaac'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showQuotePack && <QuotePackModal client={clientData} user={user} onClose={() => setShowQuotePack(false)} showToast={showToast} loadAll={loadAll} />}
      {showCoverConfirmed && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', zIndex: 200, overflowY: 'auto' }} onMouseLeave={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
          <CoverConfirmedModal client={clientData} user={user} onClose={() => setShowCoverConfirmed(false)} showToast={showToast} loadAll={loadAll} />
        </div>
      )}
      {showCallScript && <CallScriptModal onClose={() => setShowCallScript(false)} policyType={clientData.policy_type || factFinds[0]?.type || ''} initialTab={callScriptInitialTab} />}
    </div>
  )
}
